/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */
import { AfterViewInit, Component, effect, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApolloQueryResult, NetworkStatus } from '@apollo/client/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { Calendar, CalendarOptions, EventApi, EventContentArg, EventDropArg, EventInput } from '@fullcalendar/core';

import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { EventResizeDoneArg, ThirdPartyDraggable } from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'; // a plugin!
import timeGridPlugin from '@fullcalendar/timegrid';
import { dayJsFullCalendarTimeZonePlugin, dayjs } from '@karve.it/core';
import { AssetService } from '@karve.it/features';
import { ListAssetsOutput } from '@karve.it/interfaces/assets';
import { ZoneDir } from '@karve.it/interfaces/common.gql';
import { Store } from '@ngrx/store';
import { QueryRef } from 'apollo-angular';

import { capitalize, cloneDeep, intersection, union } from 'lodash';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { Subject, combineLatest, of, take } from 'rxjs';
import { filter, skip , debounceTime } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';

import { AvailabilityGQL, AvailabilityOutput, AvailabilityQuery, AvailabilityQueryVariables, BulkEditCalendarEventGQL, CalendarEventForDispatchFragment, EditCalendarEventGQL, EditCalendarEventInput, FullCalendarEventFragment, GetConfigValuesGQL, ProphetMetrics, ProphetMetricsGQL, ScheduleEventsGQL, ScheduleEventsQuery, ScheduleEventsQueryVariables, SingleEditInput, ViewDatesGQL, ViewDatesQuery, ViewDatesQueryVariables } from '../../generated/graphql.generated';
import { AppMainComponent } from '../app.main.component';

import { AssetWithConfig } from '../availability/availability.interfaces';
import { CalendarHelperService } from '../calendar-helper.service';
import { BOOK_OFF_EVENT_TYPE, JOB_EVENT_TYPES, MIN_EVENT_RENDER_TIME, ADDITIONAL_EVENT_TYPES as SUPPORTING_EVENT_TYPES, eventTypeInfoMap , DATE_PARAM_FORMAT, EventAttendeeRoles, eventTypeInfoMapV2 } from '../global.constants';
import { isFinalizedInvoice } from '../invoices/invoices.utils';
import { strToTitleCase } from '../js';
import { BrandingService } from '../services/branding.service';
import { DetailsHelperService } from '../services/details-helper.service';
import { FreyaHelperService } from '../services/freya-helper.service';
import { FreyaMutateService } from '../services/freya-mutate.service';
import { FreyaNotificationsService } from '../services/freya-notifications.service';
import { FullCalendarHelperService } from '../services/full-calendar-helper.service';
import { PermissionService } from '../services/permission.service';
import { ResponsiveHelperService } from '../services/responsive-helper.service';
import { TimezoneHelperService } from '../services/timezone-helper.service';
import { assetTypesDropdown, formatCalendarAssetTitle } from '../shared/assets/assets';
import { FCEventHolder, getEventCustomer } from '../shared/event-location/calendarevent.util';
import { selectFeature } from '../state/featureFlags.store';
import { ensureUnixSeconds } from '../time';
import { firstInitialLastName, formatUserName } from '../users/users.utils';
import { DataError, parseGraphqlErrors } from '../utilities/errors.util';
import { WatchQueryHelper } from '../utilities/watchQueryHelper';

import { DispatchCalendarRemarksDialogActions } from './dispatch/components/dispatch-calendar-remarks-dialog/dispatch-calendar-remarks-dialog.actions';
import { dispatchCalendarRemarksDialogFeature } from './dispatch/components/dispatch-calendar-remarks-dialog/dispatch-calendar-remarks-dialog.feature';
import { confirmUnsavedScheduleChanges } from './leave-schedule.guard';
import { pacingBaseline } from './pacing-card/pacing-card.component';
import { ScheduleControlsService, ScheduleControlsConfig, ControlsSummary } from './schedule-controls/schedule-controls.service';
import { ExportableEvent, generateScheduleHtml } from './schedule-export.util';
import { selectScheduleSeedDate } from './schedule-seed-date';
import { RESOURCE_AREA_HEADER } from './schedule.constants';

import { sortAssetsForSchedule } from './schedule.util';

export type EventChange = (EventResizeDoneArg | EventDropArg) & {
  notifyAttendees: boolean;
  status: 'Pending' | 'Saving' | 'Saved' | 'Failed';
  hasConflict: boolean;
};

// Define the view type mappings and session storage keys
export const SCHEDULE_VIEW_TYPE_MAP = {
  'resourceTimelineDay': 'day',
  'resourceTimelineWeek': 'week',
  'dayGridMonth': 'month',
};

const REVERSE_SCHEDULE_VIEW_TYPE_MAP = {
  day: 'resourceTimelineDay',
  week: 'resourceTimelineWeek',
  month: 'dayGridMonth',
};

/** View types shaped as a single day (the Day tab and its vertical variant).
 *  The remembered-day memory is written from these renders only — week and
 *  month navigation must not overwrite the day the user was actually on
 *  (Ray). */
const DAY_SHAPED_VIEWS = new Set(['resourceTimelineDay', 'timeGridDay']);

@Component({
  selector: 'app-schedules',
  templateUrl: './schedules.component.html',
  styleUrls: ['./schedules.component.scss', '../availability/calendar.styles.scss'],
})
export class SchedulesComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

  // Calendar
  @ViewChild('fc', { static: false }) calendarComponent: FullCalendarComponent;
  @ViewChild('scheduleOverlay') scheduleOverlay: OverlayPanel;
  @ViewChild('filtersOverlay') filtersOverlay: OverlayPanel;

  @Output() initialized = new EventEmitter();

  @Input() showHeader = true;
  @Input() showFilters = true;
  @Input() enableAvailability = true; // Whether or not this page wants to get the availability of assets
  @Input() height = '85vh';
  // Change the height based on the number of assets
  @Input() dynamicHeight = true;
  // True if we can/are editing the events on the schedule
  @Input() editModeEnabled = false;

  // The area whose events/availability should be shown, defaults to all
  @Input() area: string;

  //to track weather component is being used in separate Schedule page or
  //in one of the Estimator steps and if it already received jobId
  @Input() scheduleForEstimator = false;
  @Input() currentJobId: null | string = null;

  @Input() jobInDifferentTimezoneWarning: string;
  @Input() timezone: string;

  // If false shows the totals for day, If true shows all events for day
  showMonthEvents = false;

  // If true shows the crew members on the card
  showCrew = false;

  // Toggles
  verticalView = false;
  hideUnavailable = false;
  hideUnused = false;
  showAllDay = false;
  spreadRevenue = false;
  showForecast = false;
  showPacing = false;

  menuOptions: MenuItem[] = [];

  /** Pencil (edit mode) visibility: same permission pair as the old header button. */
  // Getter, not a field: permissions load asynchronously and checkPermission
  // returns false until then — a field would freeze the construction-time
  // value into the controls config (CodeRabbit).
  public get canEditSchedule(): boolean {
    return this.permissions.checkPermission('calendarEvents.edit') ||
      this.permissions.checkPermission('frontend.editSchedule');
  }

  private controlsConfig: ScheduleControlsConfig;
  private destroyed = false;

  /**
   * The schedule page's shared controls bar is the single date knob now. Bar
   * changes reach the FullCalendar via fcHelper.changeCalendarDate; the
   * calendar's own navigation (datesSet handler) writes back to the service
   * so the bar + URL stay in sync. Same-day no-ops break the loop.
   */
  private pendingControlsDate: Date;
  /**
   * Date string of a pending-goto we dispatched from the first datesSet. Its
   * follow-up datesSet is OUR OWN echo of a control-driven navigation, not a
   * user/programmatic view change — the write-back must not run for it or a
   * late follow-up datesSet (cold load: FullCalendar renders the pending goto
   * seconds after mount) would rewrite a window seeded in that gap. Cleared
   * on the first datesSet after the goto, matching or not.
   */
  private pendingGotoEcho: string | null = null;
  /** Remembers the last day the user was actually on while switching
   *  schedule tabs (Ray). Entering Day/Week/Month keeps this day when it
   *  falls inside the target view's displayed window; otherwise the view
   *  opens on that window's first day and the memory resets there.
   *  Per-tab (sessionStorage) — a fresh browser tab starts unremembered. */
  private rememberedDay: string | null = null;
  /** Set by changeView's remembered-day planner for a Day-tab switch: the
   *  first Day-shaped datesSet after the switch is the switch's own
   *  transient landing (the window's anchor before the planned goto) and
   *  must not overwrite the memory the planner just set. */
  private daySwitchPending = false;
  /** The route.paramMap subscriber doubles as boot and tab-switch handler;
   *  its first emission is the boot (a deep link / restore must keep its
   *  ?date= and reset the remembered day to the landed day, not plan a
   *  switch around a possibly stale memory from a previous visit). */
  private routeParamNavSeen = false;
  private controlsDateEffect = effect(() => {
    const d = this.controls.date();
    if (!d) { return; }
    // Compare the calendar's DISPLAYED day (read in the calendar's own
    // timezone) with the picker's local date label: getDate() is an instant
    // whose browser-local day can differ from the calendar's day when the
    // two timezones disagree, which would treat a real navigation as a
    // no-op (CodeRabbit).
    const calDate = this.calendar ? dayjs(this.calendar.getDate()).tz(this.getCalendarTimezone()) : null;
    const pickerDate = dayjs(d);
    if (calDate && calDate.format('YYYY-MM-DD') === pickerDate.format('YYYY-MM-DD')) {
      return;
    }
    if (this.calendar) {
      // Deferring keeps the mutation outside the effect's reactive frame: the
      // gotoDate → datesSet chain dispatches a router navigation
      // SYNCHRONOUSLY, and writing the resulting ROUTER_REQUEST store signal
      // inside an effect throws NG0600 on every Previous/Next/Today click in
      // Day/Week/Month tabs (Ray review). queueMicrotask is the same
      // convention as the datesSet writes below — the same-day guards keep
      // the deferred writes loop-free.
      queueMicrotask(() => this.setCalendarDate(d));
    } else {
      // The bar can set the date before ngAfterViewInit creates the
      // FullCalendar API (deep links: /schedule/day?date=...). Applied on
      // the first datesSet (a goto before the initial render is dropped).
      this.pendingControlsDate = d;
    }
  });
  prophetMetrics: ProphetMetrics | null = null;

    // This subject is fired when the user changes dates/view, the subscription uses a debounce to filter unecessary backend requests
    scheduleDebounce = new Subject<void>();
    /** Completes one reload: the controls bar's refresh button gates on it
     *  (spinner + disabled until it emits, Ray — no refresh toast). Fired
     *  when the events query lands non-loading after a bar-initiated
     *  refresh. */
    private refreshDone$ = new Subject<void>();

    calendar: Calendar;
  currentViewTotalRevenue = 0;
  currentDayViewStats: NonNullable<ViewDatesQuery['viewDates']>[0] | null = null;

  subs = new SubSink();
  timeOuts: NodeJS.Timeout[] = [];

  // Assets
  assets: AssetWithConfig[];
  assetsQueryRef: QueryRef<ListAssetsOutput>;
  noAvailableAssetsWarning = '';

  // Calendar Events
  calendarEvents: CalendarEventForDispatchFragment[] = [];
  /** id→event map refreshed on every calendarEvents replacement; eventContent
   *  resolves each rendered card through this instead of an O(events²) find()
   *  over ~2–3k payloads on every render. */
  calendarEventsById = new Map<string, CalendarEventForDispatchFragment>();
  calendarEventQueryRef: QueryRef<ScheduleEventsQuery, ScheduleEventsQueryVariables>;
  calendarEventDataErrors: DataError[];

  networkLoading = true;
  scheduleQH: WatchQueryHelper = {
    loading: true,
  };

  // Availability
  availabilities: AvailabilityOutput[];
  availabilityQueryRef: QueryRef<AvailabilityQuery, AvailabilityQueryVariables>;

  // View Dates
  viewDatesQueryRef: QueryRef<ViewDatesQuery, ViewDatesQueryVariables>;
  viewDatesData: NonNullable<ViewDatesQuery['viewDates']> = [];
  startSeconds: number; // The Starting Time for the calendar in unix seconds
  endSeconds: number; // The Ending Time for the calenar in unix seconds

  // Editing
  // List of all changes applied to events
  eventEdits: EventChange[] = [];
  // List of edits that can be redone
  undoneEventEdits: EventChange[] = [];
  // List of only the most recent changes for any given event
  latestEventEdits: EventChange[] = [];
  // Max number of events that can be edited concurrently, WILL BE VARIABLE IN THE FUTURE
  maximumEditableEvents = 5;
  // The number of events that are conflicting after an event change or rechecking the schedule
  eventsWithConflictsCount = 0;

  notifyAttendees = true;

  // DIALOGS
  discardEditsDialogVisible = false;

  // SAVING
  isSaving = false;
  checkingForConflicts = false;
  reloadClicked = false;

  // Managed FullCalendar event batches. Each category (schedule events,
  // availability, view-dates) is tracked as one replaceable batch so a
  // refresh never needs a full store scan (the old removeEventsOfTypes via
  // calendar.getEvents() materialized every event to filter them); adds and
  // removes stay inside the caller's single batchRendering.
  //
  // Note: swapping a ~2k-event payload through `addEventSource({ events })`
  // was A/B-measured at 4-6x the wall cost of per-event `addEvent` inside
  // that same batchRendering (FC source adds re-parse via the source/fetch
  // pipeline), which is why batches track EventApis instead of sources.
  private eventBatches: { [key: string]: EventApi[] } = {};

  /** Removes the previous batch for `key` and re-adds `events`.
   *  Call inside batchRendering like the addEvent loops it replaces. */
  private replaceBatch(key: string, events: EventInput[]): void {
    if (!this.calendar) { return; }
    const previous = this.eventBatches[key];
    if (previous) {
      for (const api of previous) { api.remove(); }
    }
    const added: EventApi[] = [];
    for (const event of events) {
      const api = this.calendar.addEvent(event);
      if (api) { added.push(api); }
    }
    this.eventBatches[key] = added;
  }

  // TODO: Break this out into a service value or a seperate file
  calendarOptions: CalendarOptions = {
    firstDay: 1, // weeks start Monday (bar chips, ops windows and labels agree)
    headerToolbar: false,
    plugins: [
      resourceTimelinePlugin,
      interactionPlugin,
      dayGridPlugin,
      timeGridPlugin,
      dayJsFullCalendarTimeZonePlugin
    ],
    timeZone: this.timeZoneHelper.getCurrentTimezone(), // will be overriden in OnChanges with job timezone
    eventMinWidth: 0.001,
    initialView: 'resourceTimelineDay',
    initialDate: this.getLastSavedDate(),
    schedulerLicenseKey: environment.fullCalendarKey,
    eventDisplay: 'block', // Without this dayGrid events don't have backgrounds
    resourceAreaHeaderContent: RESOURCE_AREA_HEADER,
    resourceAreaWidth: this.fcHelper.getResourceAreaWidth(),
    stickyFooterScrollbar: true,
    nowIndicator: true,
    // Requirement: time now indicator moves every minute instead of jumping hour to hour
    nowIndicatorSnap: false,
    allDaySlot: false,
    titleFormat: { // will produce something like "Tuesday, September 18, 2018"
      month: 'long',
      year: 'numeric',
      day: 'numeric',
      weekday: 'short'
    },
    moreLinkDidMount: (arg) => {
      // @ts-ignore
      const date = arg.el.closest('.fc-day').dataset.date;

      // Calculate start and end of day in Unix seconds to avoid heavy DayJS operations inside the loop
      const dayStartUnix = dayjs.tz(date, this.timezone).startOf('day').unix();
      const dayEndUnix = dayjs.tz(date, this.timezone).endOf('day').unix();

      const eventsForDay = this.calendarEvents.filter((ev) => {
        return ev.start >= dayStartUnix && ev.start <= dayEndUnix;
      });

      const content = document.createElement('div');
      content.classList.add('content');

      const assetTypes = this.scheduleFilters.get('assetTypes').value;
      const filteredEventsForDay = eventsForDay.filter(event => !assetTypes.length || event.assets.some((asset) => assetTypes.includes(asset.type)));
      const totalEl = document.createElement('div');
      if (filteredEventsForDay.length > 0) {
        const formattedDate = this.timezoneHelper.getDayJsTimeZoneObject(date).format('YYYY-MM-DD');
        totalEl.classList.add('total');
        totalEl.style.color = 'var(--text-color)';
        totalEl.title = `Discounted Subtotal for ${formattedDate}`;
        const totalAmount = (filteredEventsForDay.reduce((total, current) => total + current.discountedSubTotal, 0) / 100).toFixed(2);
        totalEl.innerText = `$${totalAmount}`;
      }

      const eventBadgesEl = document.createElement('div');
      eventBadgesEl.classList.add('badges-container');
      const eventTypesToDisplay = {};

      for (const event of filteredEventsForDay) {
        if (event.type === 'book-off') {
          continue;
        }
        if (eventTypesToDisplay[event.type]) {
          eventTypesToDisplay[event.type].push(event);
          continue;
        }
        eventTypesToDisplay[event.type] = [event];
      }
      // Create a badge for every event type present on the day
      for (const eventType of Object.keys(eventTypesToDisplay)) {
        const badge = document.createElement('div');
        badge.title = `${eventTypesToDisplay[eventType]?.length} ${capitalize(eventType)} events`;
        badge.innerText = `${eventTypesToDisplay[eventType]?.length}`;

        const backgroundColor = eventTypeInfoMap[eventType]?.backgroundColor || 'primary-color';

        badge.style.backgroundColor = `var(--${backgroundColor})`;
        eventBadgesEl.append(badge);
      }

      content.append(totalEl, eventBadgesEl);

      arg.el.append(content);
    },
    moreLinkClick: (info) => {
      const tzDate = dayjs(info.date).add(1, 'day').toDate();
      this.calendar.gotoDate(tzDate);
      this.calendar.changeView('resourceTimelineDay');
    },
    moreLinkContent: (_args) => ({ domNodes: [document.createElement('div')] }),
    dayMaxEvents: 0,
    views: {
      dayGridMonth: { // name of view
        titleFormat: { month: 'long', year: 'numeric' },
      },
      resourceTimelineWeek: {
        titleFormat: { month: 'long', day: 'numeric', year: 'numeric' },
        nowIndicator: true,
        // Requirement: time now indicator moves every minute instead of jumping hour to hour
        nowIndicatorSnap: false,
      },
      resourceTimelineDay: {
        nowIndicator: true,
        // Requirement: time now indicator moves every minute instead of jumping hour to hour
        nowIndicatorSnap: false,
      }
    },
    dayCellDidMount: (arg) => {
      if (arg.view.type !== 'dayGridMonth') { return; }
      const tz = this.getCalendarTimezone();
      const dateStr = dayjs(arg.date).tz(tz).format('YYYY-MM-DD');
      const remarks = this.dateRemarks.filter((r) => r.date === dateStr);
      for (const remark of remarks) {
        this.insertRemarkIntoCell(arg.el, remark);
      }
    },
    resourceLabelContent: (info) => (
      // Loading placeholders get a label mirroring the real resource label's
      // shape (44px centred bold title) so the loaded board doesn't shift
      // the layout (Ray; R2): the real label component needs an asset id to
      // render anything, so the placeholder builds the same DOM instead —
      // the styles come from schedules.component.scss's ::ng-deep rules.
      info.resource?.extendedProps?.skeletonResource
        ? SchedulesComponent.buildSkeletonLabel(info.resource.title as string)
        : this.fcHelper.generateResourceLabel(info, this.assets as any, this.showCrew)
    ),
    resources: [
      {
        id: 'loading',
        title: 'Loading ...',
      }
    ],
    resourceOrder: 'order',
    eventDidMount: (info) => {
        if (info.event.extendedProps?.type === 'view-dates') {
             const harness = info.el.closest('.fc-daygrid-event-harness');
             if (harness instanceof HTMLElement) {
                 harness.style.visibility = 'visible';
             }
        }
    },
    eventMouseEnter: (info) => {
      if (this.editModeEnabled) { return; }
      if (this.responsiveHelper.isSmallScreen) { return; }
      const ce: FullCalendarEventFragment = info.event?.extendedProps?.event;

      if (!ce) {
        return;
      }

      if (ce.type === BOOK_OFF_EVENT_TYPE) {
        return;
      }

      return this.fcHelper.createEventHover(info, ce, this.timezone);
    },
    eventMouseLeave: () => this.clearOverlays(),
    eventContent: (event) => {
      const hidden = this.resolveEventHidden(event);

      // O(1) id lookup; the previous find() multiplied the whole event list by
      // each card FullCalendar rendered (~2k cards ⇒ ~4M comparisons per render).
      const calendarEvent = this.calendarEventsById.get(event.event._def.groupId);

      let eventEl;

      if (event.event.extendedProps?.loadingSkeleton) {
        // Loading placeholder bar on a placeholder resource row: a PrimeNG
        // shimmer (class .p-skeleton is global CSS), sized by CSS (Ray:
        // loaders are real calendar events, not a separate overlay).
        const bar = document.createElement('div');
        bar.className = 'p-skeleton skel-loading-bar';
        return { domNodes: [bar] };
      }

      if (event.event.extendedProps?.type === 'view-dates') {
        const data = event.event.extendedProps.data;
        const el = this.fcHelper.createViewDatesCard(data, this.showForecast, this.showPacing, this.prophetMetrics);
        return { domNodes: [el] };
      } else if (event.event.extendedProps?.type === 'availability') {
        eventEl = this.fcHelper.createFreyaAvailability(hidden);
      } else if (calendarEvent?.type === 'book-off') {
        eventEl = this.fcHelper.createBookedOffEvent(calendarEvent, this.calendar.view.type);
      } else {
        const hasBeenEdited = Boolean(this.editModeEnabled && this.latestEventEdits.find((edit) => edit.event.id === event.event.id));

        let disabled = false;

        if (this.freyaHelper.lockDate > calendarEvent.end) {
          disabled = true;
        }

        if (calendarEvent.invoices?.some(isFinalizedInvoice)) {
          disabled = true;
        }

        // Do not style any events as disabled unless edit mode is enabled
        if (!this.editModeEnabled) {
          disabled = false;
        }

        const resources = event.event.getResources();
        const allDayCrew = resources[0]?.extendedProps?.allDayCrew || [];

        // Pass allDayCrew as the 'commonCrew' argument to compare against
        eventEl = this.fcHelper.createFreyaEvent(event, calendarEvent, this.calendar, hidden, this.editModeEnabled, hasBeenEdited, disabled, false, this.showCrew, allDayCrew);
      }

      if (!eventEl) { return; } // Event was removed

      return { domNodes: [eventEl] };
    },
    eventResizableFromStart: false,
    eventDurationEditable: false,
    // Sets the granularity for moving events
    snapDuration: '00:05:00',
    // Sets the frequency of the lines
    slotDuration: '01:00:00',
    slotLabelInterval: '02:00:00',
    // Sets the start and end times for the day
    slotMinTime: '06:00:00',
    slotMaxTime: '22:00:00',
    navLinks: false,
    datesSet: (info) => {
      const tz = this.getCalendarTimezone();
      // Interpret currentStart in the business timezone to get the correct date string.
      // info.view.currentStart is a JS Date (UTC instant); without an explicit tz it would
      // be interpreted in the browser's local timezone, which can be a different day when the
      // browser and business are in different timezones (e.g. browser=America/Denver,
      // business=America/Winnipeg).
      const dateStr = dayjs(info.view.currentStart).tz(tz).format('YYYY-MM-DD');
      const formattedStart = dayjs(info.view.currentStart).tz(tz).format('YYYY-MM-DDTHH:mm:ssZ');

      // Build a local-timezone Date at noon for the correct calendar date so that PrimeNG's
      // p-calendar (which renders in browser local time) displays the right date regardless of
      // any offset between the browser and business timezones.
      this.selectedDate = new Date(`${dateStr}T12:00:00`);
      // A date that arrived from the shared controls bar before the
      // calendar API existed (deep links: /schedule/day?date=...). Consume
      // it on the FIRST datesSet render; gotoDate before FullCalendar's
      // initial render is silently dropped, so navigate once the view is
      // live and let the follow-up datesSet sync everything. The first
      // datesSet fires during the calendar's mount, before
      // ngAfterViewInit assigns this.calendar — use the event's own
      // calendar API handle instead.
      if (this.pendingControlsDate) {
        const pending = this.pendingControlsDate;
        this.pendingControlsDate = null;
        if (!dayjs(pending).isSame(this.selectedDate, 'day') && info?.view?.calendar) {
          this.pendingGotoEcho = dayjs(pending).format('YYYY-MM-DD');
          info.view.calendar.gotoDate(this.pendingGotoEcho);
          return;
        }
      }
      // The pending goto's follow-up datesSet is our own echo — its render is
      // exactly what the controls already describe, so the write-back must
      // not run for it or a late one (cold load: FullCalendar renders the
      // pending goto seconds after mount) would rewrite a window seeded in
      // the gap. Data-fetch/URL-caching still run below: the echo IS the
      // render of the displayed week.
      let skipWriteBack = false;
      if (this.pendingGotoEcho) {
        skipWriteBack = dayjs(this.selectedDate).isSame(this.pendingGotoEcho, 'day');
        this.pendingGotoEcho = null;
      }
      // Write the calendar's own navigation back to the shared controls
      // bar (URL + bar picker) only when it actually moved the day OR the
      // retained end doesn't match the displayed view's semantics: the
      // initial render must not clobber the bar's bootstrapped &date= (deep
      // links seed the service before the calendar exists), and a range
      // carried in from another tab must not survive into a differently
      // shaped view (map range → week = the whole displayed week; map range
      // → month/day = single anchor). The setters' same-day guards keep the
      // deferred writes loop-free.
      const controlsDate = this.controls.date();
      const controlsDateTo = this.controls.dateTo();
      const isWeekDatesSet = String(info.view.type || '').toLowerCase().includes('week');
      const viewStart = dayjs(this.selectedDate);
      const expectedEnd = isWeekDatesSet ? viewStart.add(6, 'day') : null;
      const endMatches = isWeekDatesSet
        ? !!controlsDateTo && dayjs(controlsDateTo).isSame(expectedEnd, 'day')
        : !controlsDateTo;
      if (!skipWriteBack && (!controlsDate || !dayjs(controlsDate).isSame(this.selectedDate, 'day') || !endMatches)) {
        // FullCalendar flushes its React render synchronously, which can put
        // this datesSet inside this component's controlsDateEffect; defer
        // the signal writes out of any reactive context (NG0600). The
        // setters' same-day guards keep the deferred writes loop-free.
        // Capture the date now and guard on a real destruction flag —
        // controlsConfig is not a reliable lifetime guard (the first
        // datesSet runs during calendar mount, before it is set), and a
        // stale write after destruction could clobber another tab's
        // just-seeded date on the shared root-scoped service.
        const dateToWrite = this.selectedDate;
        const endToWrite = isWeekDatesSet ? viewStart.add(6, 'day').toDate() : null;
        queueMicrotask(() => {
          if (!this.destroyed) {
            this.controls.setDate(dateToWrite);
            if (isWeekDatesSet && endToWrite) {
              this.controls.setDateTo(endToWrite);
            }
          }
        });
      }
      // The remembered day (Ray): day-shaped renders mean the user is on
      // that day (prev/next, picker, deep links, drill-ins). Switch-driven
      // renders instead consume daySwitchPending — changeView planned the
      // day before switching and must not let a transient landing overwrite
      // it. Evaluated before the viewInitialized bail-out so boot renders
      // seed the memory too.
      if (DAY_SHAPED_VIEWS.has(info.view.type)) {
        if (this.daySwitchPending) {
          this.daySwitchPending = false;
        } else {
          this.setRememberedDay(dateStr);
        }
      }
      this.calendarHelper.displayedDateChanged(formattedStart, info.view.type);
      
      // If view is not initialized (initial load), skip. 
      // ngAfterViewInit will set viewInitialized and call refetchData.
      if (!this.viewInitialized) { return; }

      this.setCalendarTitle();
      this.updateMenu();
      
      // Refetch only when the displayed window actually moved (see
      // lastDatesSetWindow): repeated datesSet echoes must not refetch.
      const winKey = `${info.view.type}|${dayjs(info.view.currentStart).tz(tz).format('YYYY-MM-DD')}|${dayjs(info.view.currentEnd).tz(tz).format('YYYY-MM-DD')}`;
      if (winKey !== this.lastDatesSetWindow) {
        this.lastDatesSetWindow = winKey;
        this.refetchData();
      }

      const [_blank, currentPage] = this.router.url.split('/');

      if (currentPage.includes('schedule')) {
        // Set the date parameter in the url
        this.cacheDateAndView(dateStr, info.view.type);
      }
    },
    dateClick: (info) => {
      if (info.view.type === 'dayGridMonth') {
        const drillIn = () => {
          this.calendar.gotoDate(info.dateStr);
          this.changeView('resourceTimelineDay');
        };
        // Month-cell drill-in must not silently drop unsaved edits (#7275).
        if (this.editModeEnabled && this.latestEventEdits?.length) {
          confirmUnsavedScheduleChanges(this.confirmationService).subscribe((leave) => {
            if (leave) {
              this.discardPendingEdits();
              drillIn();
            }
          });
          return;
        }
        drillIn();
      }
    },
    navLinkWeekClick: (date, event) => {
      event.preventDefault();
    },
    eventClick: (info) => {
      // Handle double click
      if (info.jsEvent.detail === 2) {
        const job = info.event.extendedProps.event?.job;
        if (job?.id) {
          this.router.navigate(['/jobs', job.id]);
          return;
        }
      }

      const eventType = info.event?.extendedProps?.type;

      if (eventType === 'view-dates') {
          // If in aggregate view, click takes to day view. The drill-in
          // targets THIS day explicitly — skip the remembered-day planner
          // (gotoDate below positioned the calendar); the landing render
          // writes the memory via datesSet instead (Ray).
          const date = info.event.startStr;
          this.calendar.gotoDate(date);
          this.changeView('resourceTimelineDay', { rememberDay: false });
          // Also need to switch back to showing events?
          // If we are in "Show Events" = false (aggregate), switching to Day view will use normal logic (if day view logic is separate).
          // But `setMaxEvents` sets `showMonthEvents`.
          // When switching view, `isMonthView` becomes false.
          // `retrieveEvents` will use normal query.
          // So this should work fine.
          return;
      }

      if (eventType === 'availability') {
        // DISABLED, reworked version will be implemented later
        // this.selectAvailability(info.jsEvent.offsetX, info.el.offsetWidth, info.event._def.resourceIds[0], info.event.extendedProps.availability);
        return; // No event to show for availability
      }

      let detailsType: string;
      let detailsItem: any;

      if (info.event.extendedProps.eventType === BOOK_OFF_EVENT_TYPE) {
        detailsType = 'book-off-event';
        const event = info.event?.extendedProps?.event;
        if (!event) { return; }
        detailsItem = event;
      } else if (['travel-time', 'calendar-event'].includes(eventType)) {
        detailsType = 'calendar-event';
        const event = info.event?.extendedProps?.event;
        if (!event) { return; }
        detailsItem = event;
      }

      if (detailsType && detailsItem) {
        // If showing event details will cause right panel to open
        // clear overlays as otherwise mouse might leave event without firing eventMouseLeave
        if (!this.detailsHelper.rightPanelOpen) {
          this.clearOverlays();
        };
        this.detailsHelper.open(detailsType, { id: detailsItem.id });
      }


    },
    events: [],
    eventOrder: 'start',
    eventResize: (info: EventResizeDoneArg) => {
      // Pending events do not send notifications
      const isPending = info.event?._def.extendedProps?.event?.status === 'Pending';
      this.logNewEdit({ ...info, notifyAttendees: !isPending, status: 'Pending', hasConflict: false });
      this.checkForConflicts();
    },
    eventDrop: (info: EventDropArg) => {
      // Pending events do not send notifications
      const isPending = info.event?._def.extendedProps?.event?.status === 'Pending';

      this.logNewEdit({ ...info, notifyAttendees: !isPending, status: 'Pending', hasConflict: false });
      this.checkForConflicts();
    },
    // Do not automatically scroll the calendar when dragging an event on mobile (makes dragging awkward)
    dragScroll: !this.responsiveHelper.isSmallScreen,
    longPressDelay: this.responsiveHelper.isSmallScreen ? this.responsiveHelper.dragDelay : undefined
  };

  // True if the events have been loaded
  eventsInitialized = false;

  // True if the view has been initialized from URL/Storage
  viewInitialized = false;

  /** Key of the last datesSet window that already triggered a refetch.
   *  FullCalendar emits datesSet for every render (including echoes of the
   *  very flow that just rendered a window); refetching on each echo turned a
   *  single view switch into a refetch→render→datesSet→refetch chain
   *  (measured 3+ refetches/switch) that delayed the final data render.
   *  Reloads/filters/zone changes go
   *  through other refetchData() callers and stay unguarded. */
  private lastDatesSetWindow: string | null = null;

  /** Placeholder "Loading…" rows shown on the REAL resource timeline while a
   *  switch / first load fetches (Ray: use actual FullCalendar resources for
   *  the loaders; the old custom overlay div was retired). They are removed
   *  again by the first completed emission (before the real assets are
   *  converted in) and re-install safely on rapid double switches. */
  private readonly LOADING_RESOURCE_COUNT = 8;

  /**
   * Paint-then-run: schedules fn on a macrotask AFTER the next frame paints.
   * rAF runs immediately before the browser paints; the setTimeout inside it
   * queues fn for the next macrotask.
   */
  private afterPaint(fn: () => void) {
    requestAnimationFrame(() => {
      setTimeout(fn, 0);
    });
  }

  // Constants
  assetTypes = assetTypesDropdown;
  systemEventTypes = [...JOB_EVENT_TYPES];
  supportingEventTypes = SUPPORTING_EVENT_TYPES;

  // Filters
  scheduleFilters: UntypedFormGroup = new UntypedFormGroup({
    assetTypes: new UntypedFormControl([]),
    eventTypes: new UntypedFormControl([]),
    products: new UntypedFormControl([]),
    // supportingEventTypes: new FormControl([]),
  });

  filtersCollapsed = true;

  get activeFilterCount(): number {
    const { assetTypes, eventTypes, products } = this.scheduleFilters.value;
    return (assetTypes?.length || 0) + (eventTypes?.length || 0) + (products?.length || 0);
  }

  /** The bar's filter button opens this tab's overlay (anchored to the
   *  bar's button so the panel pops near the controls, Ray). */
  toggleFilters(event: Event): void {
    this.filtersOverlay?.toggle(event);
  }

  icalExportFeatureEnabled$ = this.store.select(selectFeature('ical-export'));
  canExportIcal = false;
  canSetConfig = false;
  isCalendarRemarksDialogOpen$ = this.store.select(dispatchCalendarRemarksDialogFeature.selectIsOpen);
  DispatchCalendarRemarksDialogActions = DispatchCalendarRemarksDialogActions;

  currentView = 'resourceTimelineDay';
  selectedDate: Date;

  dateRemarks: Array<{ date: string; text: string; severity: string }> = [];

  private resizeObserver: ResizeObserver;
  private intersectionObserver: IntersectionObserver;

  constructor(
    // Angular
    private router: Router,
    private route: ActivatedRoute,
    public store: Store,
    private el: ElementRef,
    // Helpers
    public responsiveHelper: ResponsiveHelperService,
    private detailsHelper: DetailsHelperService,
    private localNotify: FreyaNotificationsService,
    private freyaHelper: FreyaHelperService,
    private fcHelper: FullCalendarHelperService,
    private confirmationService: ConfirmationService,
    private calendarHelper: CalendarHelperService,
    private brandingService: BrandingService,
    private timeZoneHelper: TimezoneHelperService,
    private freyaMutate: FreyaMutateService,
    private permissions: PermissionService,
    private controls: ScheduleControlsService,

    // Components
    private appMain: AppMainComponent,
    // GQL
    private assetService: AssetService,
    private editCalendarEventGQL: EditCalendarEventGQL,
    private scheduleEventsGQL: ScheduleEventsGQL,
    private availabilityGQL: AvailabilityGQL,
    private timezoneHelper: TimezoneHelperService,
    private bulkEditCalendarEventGQL: BulkEditCalendarEventGQL,
    private viewDatesGQL: ViewDatesGQL,
    private prophetMetricsGQL: ProphetMetricsGQL,
    private getConfigValuesGQL: GetConfigValuesGQL,
  ) { }

  @HostListener('document:keydown.control.z')
  onUndo() {
    if (!this.editModeEnabled) { return; }

    this.undoLastEdit();
  };

  @HostListener('document:keydown.control.y')
  onRedo() {
    if (!this.editModeEnabled) { return; }

    this.redoEdit();
  };

  @HostListener('document:keydown', ['$event'])
  handleSaveShortcut(event: KeyboardEvent) {
    const isSaveShortcut = event.ctrlKey && event.key.toLowerCase() === 's';

    if (isSaveShortcut) {
      event.preventDefault();
      if (!this.editModeEnabled || this.isSaving) return;

      this.tryToSave();
    }
  }

  ngOnInit(): void {
    // Remembers the last day the user was on across tab switches in this
    // browser tab (Ray). Boot renders reset it to the landed day.
    this.rememberedDay = sessionStorage.getItem(environment.lskeys.scheduleRememberedDay) || null;

    this.subs.sink = this.freyaHelper.layoutScrolled.subscribe(() => {
      this.clearOverlays();
    });

    this.subs.sink = this.detailsHelper.getObjectUpdates('Events').subscribe(() => {
      this.refetchData();
    });

    this.subs.sink = this.calendarHelper.refreshScheduleData.subscribe(() => {
      this.reloadClicked = true;
      this.refetchData();
    });

    // Reload date remarks after the calendar-remarks dialog closes
    // (covers save + cancel — a re-fetch is cheap and always correct).
    this.subs.sink = this.isCalendarRemarksDialogOpen$.pipe(
      skip(1),
      filter((open) => !open),
    ).subscribe(() => this.retrieveViewDates());

    // Set the calenar date from other components
    this.subs.sink = this.fcHelper.changeCalendarDate.subscribe((date) => {
      if (!this.calendar) { return; }
      const tzDate = dayjs(date).format('YYYY-MM-DD');
      this.calendar.gotoDate(tzDate);
    });

    // This ensures that the calendar is rerendered if the container size changes to match that size.
    this.subs.sink = this.freyaHelper.containerSizeChanged.subscribe(() => {
      this.renderAfterDelay();
    });

    // Update the calendar to reflect any changes based on authentication status
    this.subs.sink = this.brandingService.currentZone().subscribe(() => {
      // skip initial behavioursubject callback
      if (!this.calendar) { return; }
      this.calendar.removeAllEvents();
      this.eventBatches = {};
      this.calendarEventsById.clear();
      this.scheduleQH.loading = true;
      this.calendarEvents = [];
      this.refetchData();
    });

    // Requirement: Merge config-based custom event types into filter dropdown
    // cache-first: the key list is static per session, so once fetched this
    // is served from cache — the op measured 1.1s of server latency and
    // re-ran on every entry into the schedule tabs.
    this.subs.sink = this.getConfigValuesGQL.fetch({ keys: ['calendarEvents.types'] }, { fetchPolicy: 'cache-first' }).subscribe((res) => {
      const configTypes = res.data?.getConfigValues?.[0]?.value;
      if (configTypes) {
        try {
          const parsed = typeof configTypes === 'string' ? JSON.parse(configTypes) : configTypes;
          const customKeys = Object.keys(parsed).filter((k) => !this.systemEventTypes.includes(k));
          if (customKeys.length) {
            this.systemEventTypes = [...this.systemEventTypes, ...customKeys];
          }
        } catch { /* invalid config value, ignore */ }
      }
    });

    this.subs.sink = this.timeZoneHelper.timezoneChanged.subscribe(() => {
      this.calendar.setOption('timeZone', this.timeZoneHelper.getCurrentTimezone());
    });

    this.selectedDate = dayjs(this.getLastSavedDate()).toDate();
    // Deep links (&date= on the bar) win over the session's last-used
    // date: the bar seeds the service during the same CD pass, so queue
    // anything that differs from this component's own seed. It is applied
    // on the calendar's first datesSet (a goto before the initial render
    // would be dropped).
    const controlsSeed = this.controls.date();
    if (controlsSeed && !dayjs(controlsSeed).isSame(this.selectedDate, 'day')) {
      this.pendingControlsDate = controlsSeed;
      this.selectedDate = controlsSeed;
    }

    // Ical Export Permission Check
    this.subs.sink = combineLatest([
      this.permissions.watchPermission('tokens.createIcalToken'),
      this.icalExportFeatureEnabled$
    ]).subscribe(([hasPermission, featureEnabled]) => {
      this.canExportIcal = hasPermission && featureEnabled;
      this.updateMenu();
    });

    // Calendar Remarks permission check
    this.subs.sink = this.permissions.watchPermission('config.set').subscribe((hasPermission) => {
      this.canSetConfig = hasPermission;
      this.updateMenu();
    });

    this.initializeToggles();

    // Refetch after 250ms without the user changing the date/view
    this.subs.sink = this.scheduleDebounce.pipe(debounceTime(250)).subscribe((_res) => {
      // Don't fetch if view hasn't been initialized yet
      if (!this.viewInitialized) { return; }

      //when click Create New Job in Estimator we run this queries right away and then rerun after job created
      //it doesn't make sense to run them when job doesn't exist yet, so here we add additional check
      if (this.scheduleForEstimator && this.currentJobId) {
        this.retrieveScheduleData();
      } else if (!this.scheduleForEstimator) {
        this.retrieveScheduleData();
      } else {
        // Estimator without a job id: fetching is intentionally skipped
        // (no loader — the bare board shows while the estimate is built).
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.timezone) {
      this.calendar?.setOption('timeZone', this.timezone);
    }
  }

  ngAfterViewInit() {

    this.calendar = this.calendarComponent.getApi();
    // The calendar was (re)created — old batch handles belong to a dead
    // instance; start clean so replaceBatch re-adds without stale removes.
    this.eventBatches = {};
    this.setCalendarTitle();

    // Load view type and date from the url otherwise use the session storage
    this.subs.sink = this.route.paramMap.subscribe((paramsMap) => {
      // First emission is the boot (deep link / restore): keep the URL's
      // date; the landing resets the remembered day via datesSet. Later
      // emissions are tab switches planned by the remembered-day rule.
      const isBootNavigation = !this.routeParamNavSeen;
      this.routeParamNavSeen = true;

      const viewType = paramsMap.get('viewType') as keyof typeof REVERSE_SCHEDULE_VIEW_TYPE_MAP;

      const calendarView = REVERSE_SCHEDULE_VIEW_TYPE_MAP[viewType] || sessionStorage.getItem(environment.lskeys.scheduleView) || REVERSE_SCHEDULE_VIEW_TYPE_MAP.day;

      const currentCalendarView = this.calendar.view.type;

      // Calculate the intended view based on toggle
      let intendedView = calendarView;
      if (this.verticalView) {
        if (calendarView === 'resourceTimelineDay') intendedView = 'timeGridDay';
        else if (calendarView === 'resourceTimelineWeek') intendedView = 'timeGridWeek';
      } else {
        // Ensure we are in horizontal if toggled off
        if (calendarView === 'timeGridDay') intendedView = 'resourceTimelineDay';
        else if (calendarView === 'timeGridWeek') intendedView = 'resourceTimelineWeek';
      }

      if (currentCalendarView !== intendedView) {
        this.changeView(intendedView, isBootNavigation ? { rememberDay: false } : undefined);
        this.currentView = intendedView;
        this.setCalendarTitle();

        // We may be setting the title after change detection has completed,
        // call this to prevent an `ExpressionCheckedAfterItHasBeenCheckedError`
        this.appMain.cd.detectChanges();

        this.viewInitialized = true;
        // Tab/view switch: the skeleton paints first and changeView (deferred
        // inside changeView, afterPaint) fires datesSet → refetchData with the
        // NEW view's window. Fetching here would use the stale view's window
        // read off calendar.view, then the datesSet pass would repeat it.
        return;
      }

      this.currentView = intendedView;
      this.setCalendarTitle();

      // We may be setting the title after change detection has completed,
      // call this to prevent an `ExpressionCheckedAfterItHasBeenCheckedError`
      this.appMain.cd.detectChanges();
      this.renderAfterDelay();

      // Boot already landing on the right view (no changeView is called):
      // timeline/timeGrid get the REAL loading placeholders instead of a
      // bare board until the first emission (Ray: loaders are actual
      // calendar resources). Month relies on its aggregate flow (per-cell
      // ghost cards). Only before the first pass — later echoes of this
      // subscriber must not reinstall loaders over live data.
      if (!this.viewInitialized &&
          (intendedView === 'resourceTimelineDay' || intendedView === 'resourceTimelineWeek' || intendedView.startsWith('timeGrid'))) {
        this.installLoadingPlaceholders(intendedView);
      }

      this.viewInitialized = true;
      this.refetchData(); // Trigger initial fetch now that view is set
    });

    this.retrieveAssets();

    // Fire event when the component is initialized, timeout allows references in parent compoennts to update
    const timeout = setTimeout(() => {
      this.initialized.emit(true);
      if (this.dynamicHeight) {
        this.setCalendarHeight('auto');
      } else {
        this.setCalendarHeight(this.height);
      }
    }, 25);

    this.timeOuts.push(timeout);

    const container = document.getElementById('droppable-area');

    new ThirdPartyDraggable(container);
    this.updateMenu();
    this.appMain.cd.detectChanges();

    // Setup ResizeObserver for header height
    const headerElement = this.el.nativeElement.querySelector('.calendar-header-wrapper');
    if (headerElement && window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          this.updateHeaderHeight(entry.target as HTMLElement);
        }
      });
      this.resizeObserver.observe(headerElement);
    }

    // Setup IntersectionObserver for visibility
    if (window.IntersectionObserver) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.calendar) {
            this.calendar.updateSize();
            // Optional: re-render if needed, but updateSize is usually enough for geometry
          }
        });
      });
      this.intersectionObserver.observe(this.el.nativeElement);
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.clearOverlays();
    this.timeOuts.forEach((t) => clearTimeout(t));
    // The bounded stuck-placeholder fallback must not fire after teardown.
    if (this.stuckPlaceholderReleaseTimer) {
      clearTimeout(this.stuckPlaceholderReleaseTimer);
      this.stuckPlaceholderReleaseTimer = null;
    }
    this.subs.unsubscribe();
    this.freyaHelper.scheduleEditModeEnabled.next(false);
    if (this.controlsConfig) {
      this.controls.clearConfig(this.controlsConfig);
    }
    this.controlsConfig = null;
  }

  // CALENDAR HELPERS

  /**
   * Returns the calendar's configured timezone — the `@Input() timezone` when used in the
   * estimator context, or the current branding timezone when used as the standalone schedule page.
   */
  private getCalendarTimezone(): string {
    return this.timezone || this.timeZoneHelper.getCurrentTimezone();
  }

  setCalendarDate(date: Date) {
    this.selectedDate = date;
    this.fcHelper.changeCalendarDate.next(date);
  }

  isMonthView() {
    return this.calendar?.view?.type === 'dayGridMonth';
  }

  /** The displayed view is a 7-cell week grid (timeline or vertical): its
   *  record window is the next-7-days span from the anchor (Ray). */
  isWeekView() {
    return String(this.calendar?.view?.type || '').toLowerCase().includes('week');
  }

  /**
   * Persists the remembered day (sessionStorage, per tab).
   */
  private setRememberedDay(dateStr: string): void {
    if (!dateStr || this.rememberedDay === dateStr) { return; }
    this.rememberedDay = dateStr;
    try {
      sessionStorage.setItem(environment.lskeys.scheduleRememberedDay, dateStr);
    } catch { /* storage unavailable — memory stays in-component */ }
  }

  /**
   * Remembers which day a tab switch should land on (Ray). Reads the LIVE
   * pre-change view (call before this.calendar.changeView): the remembered
   * day is kept when it falls inside the current window — for the
   * dayGridMonth the logical month bounds are used, not the padded grid
   * that includes neighbour weeks. Otherwise the target view's opening day
   * is the current window's first day, with the memory reset there.
   */
  private planRememberedDaySwitch(): { day: string; fromMemory: boolean } | null {
    if (!this.calendar?.view) { return null; }
    const tz = this.getCalendarTimezone();
    const view = this.calendar.view;
    let start: dayjs.Dayjs;
    if (view.type === 'dayGridMonth') {
      const current = dayjs(this.calendar.getDate()).tz(tz);
      start = current.startOf('month');
    } else {
      start = dayjs(view.currentStart).tz(tz);
    }
    const end = view.type === 'dayGridMonth'
      ? start.add(1, 'month')
      : dayjs(view.currentEnd).tz(tz);
    const remembered = this.rememberedDay;
    if (remembered) {
      // CodeRabbit round 33: compare the YYYY-MM-DD LABELS in the business
      // timezone — parsing `remembered` as browser-local midnight and
      // comparing its day boundaries against business-timezone instants
      // misclassifies the window's edge days when the browser and business
      // timezones differ. Calendar-date labels sort in calendar order.
      if (remembered >= start.format('YYYY-MM-DD') && remembered < end.format('YYYY-MM-DD')) {
        return { day: remembered, fromMemory: true };
      }
    }
    return { day: start.format('YYYY-MM-DD'), fromMemory: false };
  }

  /**
   * Clears unsaved edits without leaving edit mode (Ray, #7275): pending
   * edits are dropped before a confirmed view/tab switch — edit mode itself
   * stays on so re-entering the board keeps the edit affordances.
   */
  private discardPendingEdits() {
    this.eventEdits = [];
    this.undoneEventEdits = [];
    this.latestEventEdits = [];
  }

  /**
   * A rejected view switch must heal the URL back to the view the user
   * stayed on (#7275): tab clicks commit the URL (karve-tabview navigates
   * before the param subscription runs changeView), so on reject we rewrite
   * /schedule/:viewType with the pre-change view+date — the re-entrant param
   * emission sees intended view === current view and stays put.
   */
  private revertViewSwitchUrl(preChangeView: string) {
    const slug = SCHEDULE_VIEW_TYPE_MAP[preChangeView];
    if (!slug) { return; }
    const tz = this.getCalendarTimezone();
    const date = dayjs(this.calendar?.getDate()).tz(tz).format('YYYY-MM-DD');
    this.changeUrl(date, slug as keyof typeof REVERSE_SCHEDULE_VIEW_TYPE_MAP);
  }

  /**
   * Changes the view based on the view type, throws a warning in edit mode
   *
   * @param viewType The type of you you want to render
   * @param options rememberDay=false (boot/deep-link restores) skips the
   *   remembered-day planner: the URL's date wins and the landing reset
   *   the memory via datesSet instead (Ray).
   */
  changeView(viewType: string, options?: { rememberDay?: boolean }) {
    // Check if viewType matches current vertical/horizontal setting
    // If we click "Day" button, we pass 'resourceTimelineDay'.
    // If vertical is on, we should switch to 'timeGridDay' instead.
    
    if (viewType === 'resourceTimelineDay' && this.verticalView) {
      viewType = 'timeGridDay';
    } else if (viewType === 'resourceTimelineWeek' && this.verticalView) {
      viewType = 'timeGridWeek';
    } else if (viewType === 'timeGridDay' && !this.verticalView) {
      viewType = 'resourceTimelineDay';
    } else if (viewType === 'timeGridWeek' && !this.verticalView) {
      viewType = 'resourceTimelineWeek';
    }

    // Warn before dropping unsaved edits on a view switch (#7275): the
    // dialog copy matches the leave-schedule guard. Reject stays in place
    // (the URL committed by the tab click is healed by revertViewSwitchUrl);
    // accept discards the pending edits and proceeds with the switch.
    // Same-type calls (e.g. boot re-lands) never warn.
    const preChangeView = this.calendar?.view?.type;
    if (
      preChangeView && viewType !== preChangeView
      && this.editModeEnabled && this.latestEventEdits?.length
    ) {
      confirmUnsavedScheduleChanges(this.confirmationService).subscribe((leave) => {
        if (!leave) {
          this.revertViewSwitchUrl(preChangeView);
          return;
        }
        this.discardPendingEdits();
        this.exitEditModeIfMonth(viewType);
        this.performChangeView(viewType, options);
      });
      return;
    }

    this.exitEditModeIfMonth(viewType);
    this.performChangeView(viewType, options);
  }

  /** Month is read-only (#7275): a switch landing on Month must not keep a
   *  stale edit-mode flag — event hover (eventMouseEnter) early-returns and
   *  the Export gear item stays hidden otherwise (CodeRabbit CR e458b0c6,
   *  !2637). Called on every changeView path that proceeds with the switch. */
  private exitEditModeIfMonth(viewType: string) {
    if (viewType === 'dayGridMonth' && this.editModeEnabled) {
      this.enableEditMode(false);
    }
  }

  /**
   * Executes the actual FullCalendar view switch. Split from changeView
   * (#7275) so the unsaved-edits confirm gates the switch.
   */
  private performChangeView(viewType: string, options?: { rememberDay?: boolean }) {
    // The heavy FullCalendar render runs in the afterPaint callback. The
    // month special cases move in with the switch — they are part of the
    // same deferred mutation so the aggregated placeholders (and the
    // mandatory remove-before-switch) still happen before FC lays out the
    // new grid.
    this.afterPaint(() => {
      // Capture the LIVE pre-change view. this.currentView is set to the
      // target by the callers BEFORE this deferred callback runs, so a
      // currentView-based month-exit guard can never fire; the calendar's own
      // type is correct for this wrapper even with the deferral (direct
      // FullCalendar API switches bypass this wrapper — their leftovers are
      // released by the events-completion view-dates flush).
      const fromView = this.calendar.view.type;
      const enteringAggregateMonth = viewType === 'dayGridMonth' && !this.showMonthEvents;
      // We MUST remove all existing events BEFORE switching views, otherwise
      // FullCalendar will try to synchronously render 2000+ day view events in a month grid, causing a freeze/crash.
      // (Also drops the month ghosts when leaving the aggregate so they
      // aren't carried onto the timeline.)
      if (enteringAggregateMonth || (fromView === 'dayGridMonth' && viewType !== 'dayGridMonth')) {
        this.calendar.batchRendering(() => {
          this.calendar.removeAllEvents();
          this.eventBatches = {};
        });
      }

      // Remembered-day planner (Ray): plan from the live pre-change view,
      // then land the target view on the remembered day when it still falls
      // inside its window — otherwise on that window's first day, resetting
      // the memory (sessionStorage, per tab). Boot/deep-link restores pass
      // rememberDay:false and keep the URL's date.
      const daySwitchPlan = options?.rememberDay === false ? null : this.planRememberedDaySwitch();

      this.calendar.changeView(viewType);
      this.currentView = viewType;
      this.setCalendarHeight();

      if (daySwitchPlan) {
        const tz = this.getCalendarTimezone();
        const landed = dayjs(this.calendar.getDate()).tz(tz).format('YYYY-MM-DD');
        if (landed !== daySwitchPlan.day) {
          this.calendar.gotoDate(daySwitchPlan.day);
        }
        this.setRememberedDay(daySwitchPlan.day);
        // The switch's landing render (before our goto's echo) must not
        // overwrite the planned day via datesSet.
        if (DAY_SHAPED_VIEWS.has(viewType)) {
          this.daySwitchPending = true;
        }
      }

      if (enteringAggregateMonth) {
        // Requirement: When in month view and showMonthEvents is false, inject skeleton events (via addSkeletonEvents) within calendar.batchRendering to display aggregated month placeholders while real events load.
        // Computed AFTER the planner's goto so the placeholders cover the
        // month the switch actually lands in (a spanning week can anchor a
        // different month than the pre-change window).
        // A stuck-release in flight (round-33 major fix) must not fire into
        // the aggregate grid: the day-level list was just removed and the
        // release paths would render it back (CR round 35).
        this.skeletonPlaceholdersInstalled = false;
        if (this.stuckPlaceholderReleaseTimer) {
          clearTimeout(this.stuckPlaceholderReleaseTimer);
          this.stuckPlaceholderReleaseTimer = null;
        }
        const date = dayjs(this.calendar.getDate());
        const start = date.startOf('month').startOf('week');
        const end = date.endOf('month').endOf('week');
        this.calendar.batchRendering(() => {
          this.addSkeletonEvents(start, end);
        });
      }
      // Resource timelines get REAL "Loading…" placeholder rows with
      // shimmer bars; vertical timeGrid gets the bars without the resource
      // axis. The old separate overlay div is gone (Ray: only the actual
      // calendar loader). For month aggregate the placeholders are the
      // ghost cards injected per day by addSkeletonEvents above, so the
      // switch installs nothing here.
      if (viewType === 'resourceTimelineDay' || viewType === 'resourceTimelineWeek' ||
          viewType === 'timeGridDay' || viewType === 'timeGridWeek') {
        this.installLoadingPlaceholders(viewType);
        // Self-heal the header strip on the switch (Ray: "forecast and
        // pacing stuck loading even though the query did finish"): the
        // switch usually lands inside a window already covered by
        // viewDatesData (the same month), so the strip's day stats can be
        // recomputed from the settled view immediately instead of
        // waiting for the refetch — which may never re-emit if the
        // previous view settled the queries between the emissions
        // (cache-and-network: a refetch can be swallowed by the loading
        // guard when the window is unchanged). The refetch's completion
        // re-runs this with fresh rows anyway; this is the fast-path
        // release so the strip can't appear stuck during the gap.
        if (this.viewDatesData?.length) {
          this.calculateViewTotal();
        }
      }
    });
  }

  changeDate(dateType: 'prev' | 'next' | 'today') {
    if (this.isMonthView() && !this.showMonthEvents) {
      let date = dayjs(this.calendar.getDate());
      if (dateType === 'prev') date = date.subtract(1, 'month');
      if (dateType === 'next') date = date.add(1, 'month');
      if (dateType === 'today') date = dayjs();

      const start = date.startOf('month').startOf('week');
      const end = date.endOf('month').endOf('week');

      // Requirement: When in month view and showMonthEvents is false, inject skeleton events (via addSkeletonEvents) within calendar.batchRendering to display aggregated month placeholders while real events load
      this.calendar.batchRendering(() => {
        this.addSkeletonEvents(start, end);
      });
    }

    if (dateType === 'prev') {
      this.calendar.prev();
    }

    if (dateType === 'next') {
      this.calendar.next();
    }

    if (dateType === 'today') {
      this.calendar.today();
    }
  }

  setCalendarHeight(height?: string) {
    const resolvedHeight = height ?? 'auto';
    this.calendar.setOption('height', resolvedHeight);
    // Requirement: Disable sticky footer scrollbar when height is auto to prevent double scroll
    this.calendar.setOption('stickyFooterScrollbar', resolvedHeight !== 'auto');
  }

  private updateDateRemarks() {
    this.dateRemarks = (this.viewDatesData || [])
      .flatMap((d) => (d.remarks ?? []).map((r) => ({
        date: d.date,
        text: (r?.text ?? '').trim(),
        severity: r?.severity || 'info',
      })))
      .filter((r) => r.text.length > 0);
    if (this.isMonthView()) {
      this.refreshMonthViewRemarks();
    }
  }

  get visibleDateRemarks() {
    const viewType = this.calendar?.view?.type;
    if (viewType === 'resourceTimelineDay' || viewType === 'timeGridDay') {
      const currentDateStr = dayjs(this.selectedDate).format('YYYY-MM-DD');
      return this.dateRemarks.filter((r) => r.date === currentDateStr);
    }
    return [];
  }

  private refreshMonthViewRemarks() {
    this.el.nativeElement.querySelectorAll('.fc-day-remark').forEach((el: HTMLElement) => el.remove());
    for (const remark of this.dateRemarks) {
      const cell = this.el.nativeElement.querySelector(`.fc-daygrid-day[data-date="${remark.date}"]`);
      if (cell) {
        this.insertRemarkIntoCell(cell, remark);
      }
    }
  }

  private insertRemarkIntoCell(cellEl: HTMLElement, remark: { text: string; severity: string }) {
    const remarkEl = document.createElement('div');
    remarkEl.classList.add('fc-day-remark');
    remarkEl.textContent = remark.text;
    const topEl = cellEl.querySelector('.fc-daygrid-day-top');
    if (topEl) {
      topEl.appendChild(remarkEl);
    } else {
      cellEl.prepend(remarkEl);
    }
  }

  clearOverlays() {
    this.fcHelper.removeEventHover();
  }

  setCalendarTitle() {
    // The title itself no longer renders (the crumb carries the window —
    // Ray); only the revenue totals below it must keep computing.
    this.calculateViewTotal();
  }

  filterByEventType(eventType: string) {
    this.scheduleFilters.get('eventTypes').setValue([eventType]);

    if (!this.hideUnused) {
      this.hideUnused = true;
      this.updateToggleState('hideUnused', this.hideUnused);
    }

    this.renderAfterDelay();
    this.updateMenu();
  }

  calculateViewTotal() {
    if (!this.calendar || !this.calendar.view) { return; }

    const tz = this.getCalendarTimezone();
    const viewStart = dayjs(this.calendar.view.currentStart).tz(tz);
    const viewEnd = dayjs(this.calendar.view.currentEnd).tz(tz);

    // Update Stats
    this.currentDayViewStats = null;
    if (this.viewDatesData?.length) {
      if (this.calendar.view.type === 'resourceTimelineDay') {
        const currentDayStr = viewStart.format('YYYY-MM-DD');
        const dayData = this.viewDatesData.find(d => d.date === currentDayStr);

        if (dayData) {
          this.currentDayViewStats = dayData;
        }
      } else {
        // Aggregate for Month/Week
        this.currentDayViewStats = this.aggregateViewStats(viewStart, viewEnd);
      }
    }

    let total = 0;

    if (this.isMonthView() && !this.showMonthEvents) {
      if (this.viewDatesData) {
        this.viewDatesData.forEach(day => {
          if (!day) { return; }
          const dayDate = dayjs(day.date);
          if ((dayDate.isSame(viewStart) || dayDate.isAfter(viewStart)) && dayDate.isBefore(viewEnd)) {
            total += (day.totalRevenue || 0);
          }
        });
      }
    } else {
      if (this.calendarEvents) {
        this.calendarEvents.forEach(event => {
          const revenue = event.discountedSubTotal || 0;
          if (revenue === 0) { return; }

          const eventStart = dayjs(event.start * 1000);
          const eventEnd = dayjs(event.end * 1000);

          if (this.spreadRevenue) {
            const totalDuration = eventEnd.diff(eventStart);
            if (totalDuration <= 0) { return; }

            const overlapStart = eventStart.isAfter(viewStart) ? eventStart : viewStart;
            const overlapEnd = eventEnd.isBefore(viewEnd) ? eventEnd : viewEnd;

            if (overlapEnd.isAfter(overlapStart)) {
              const overlapDuration = overlapEnd.diff(overlapStart);
              total += revenue * (overlapDuration / totalDuration);
            }
          } else {
            if ((eventEnd.isSame(viewStart) || eventEnd.isAfter(viewStart)) && eventEnd.isBefore(viewEnd)) {
              total += revenue;
            }
          }
        });
      }
    }

    this.currentViewTotalRevenue = total;
    // The header strip mirrors this compute: same values as the removed
    // in-card strip re-published into the bar (Ray).
    this.controls.summary.set(this.buildControlsSummary());
  }

  /** The active tab's header-strip values (the removed in-card summary's
   *  own inputs and render condition — DailySummaryComponent's inputs):
   *  null = nothing in the bar (e.g. no data and no revenue yet). */
  private buildControlsSummary(): ControlsSummary | null {
    const view = this.calendar?.view?.type;
    const isDayView = view === 'resourceTimelineDay';
    const forceShowRevenue = !!view && !isDayView;
    // The card strip's own *ngIf, verbatim: day view always (skeleton while
    // stats load); other views only with revenue to show.
    if (!this.currentDayViewStats && !(this.currentViewTotalRevenue > 0 && forceShowRevenue) && !isDayView) {
      return null;
    }
    return {
      stats: this.currentDayViewStats,
      totalRevenue: this.currentViewTotalRevenue,
      showForecast: this.showForecast,
      showPacing: this.showPacing,
      isDayView,
      forceShowRevenue,
      prophetMetrics: this.prophetMetrics,
      allDays: this.viewDatesData,
      onFilterByType: (type) => this.filterByEventType(type),
    };
  }

  aggregateViewStats(viewStart: dayjs.Dayjs, viewEnd: dayjs.Dayjs) {
    if (!this.viewDatesData?.length) return null;

    const startStr = viewStart.format('YYYY-MM-DD');
    const endStr = viewEnd.format('YYYY-MM-DD');

    const days = this.viewDatesData.filter(d => {
      return d.date >= startStr && d.date < endStr;
    });

    if (!days.length) return null;

    const agg = {
      date: viewStart.format('YYYY-MM-DD'),
      actualTrucksUsed: 0,
      truckCapacity: 0,
      actualCrewUsed: 0,
      crewCapacity: 0,
      actualCrewRequired: 0,
      totalRevenue: 0,
      totalEvents: 0,
      totalEventsByType: [] as any[],
      prophet: null as any,
      pacing: null as any,
      remarks: [] as any[],
    };

    const typeCounts: Record<string, number> = {};
    const prophetOutcome = { expected_revenue: 0, expected_bookings: 0, expected_trucks: 0, expected_crew: 0 };
    let hasProphet = false;

    const pacingSum = { lastYearRevenueFinal: 0, lastYearRevenueToday: 0, pacingRevenue: 0 };
    let hasPacing = false;

    const todayStr = dayjs().tz(this.timezone).format('YYYY-MM-DD');

    days.forEach(d => {
      agg.actualTrucksUsed += d.actualTrucksUsed;
      agg.truckCapacity += d.truckCapacity;
      agg.actualCrewUsed += d.actualCrewUsed;
      agg.crewCapacity += d.crewCapacity;
      agg.actualCrewRequired += (d as any).actualCrewRequired || 0;
      agg.totalRevenue += d.totalRevenue;
      agg.totalEvents += d.totalEvents;

      d.totalEventsByType.forEach(t => {
        typeCounts[t.type] = (typeCounts[t.type] || 0) + t.count;
      });

      const isPast = d.date < todayStr;

      if (d.prophet?.outcome) {
        hasProphet = true;
      }

      if (isPast) {
        prophetOutcome.expected_revenue += d.totalRevenue;
        prophetOutcome.expected_bookings += d.totalEvents;
      } else if (d.prophet?.outcome) {
        prophetOutcome.expected_revenue += d.prophet.outcome.expected_revenue || 0;
        prophetOutcome.expected_bookings += d.prophet.outcome.expected_bookings || 0;
        // Trucks/Crew excluded for aggregate month view
      }

      if (d.pacing) {
        hasPacing = true;
        pacingSum.lastYearRevenueFinal += d.pacing.lastYearRevenueFinal || 0;
        pacingSum.lastYearRevenueToday += d.pacing.lastYearRevenueToday || 0;
        pacingSum.pacingRevenue += d.pacing.pacingRevenue || 0;
      }
    });

    agg.totalEventsByType = Object.entries(typeCounts).map(([type, count]) => ({ type, count }));

    if (hasProphet) {
      agg.prophet = { outcome: prophetOutcome };
    }

    if (hasPacing) {
      const lyBaseline = pacingBaseline(pacingSum.lastYearRevenueToday, pacingSum.lastYearRevenueFinal);
      const hasBaseline = lyBaseline > 0;
      // No-baseline sentinels match the backend: 2 (200%) with revenue, 0 without
      const perf = hasBaseline ? agg.totalRevenue / lyBaseline : (agg.totalRevenue > 0 ? 2 : 0);
      agg.pacing = {
        ...pacingSum,
        // Same formula as the per-day rows: projected = performance × LY Final
        pacingRevenue: hasBaseline ? Math.round(perf * pacingSum.lastYearRevenueFinal) : agg.totalRevenue,
        performanceToday: perf
      };
    }

    return agg;
  }

  /**
   * Determines if an event should be shown based on the filters
   *
   * @param event Full calendar event to render
   * @returns True if the event should be hidden, false if it should be shown
   */
  resolveEventHidden(event: EventContentArg): boolean {
    const props = event.event.extendedProps || {};

    const filters = this.scheduleFilters.value;

    if (filters.eventTypes?.length && (props.eventType && !filters.eventTypes?.includes(props.eventType))) {
      return true;
    }

    if (filters.supportingEventTypes?.length && (props.type && !filters.supportingEventTypes?.includes(props.type))) {
      return true;
    }

    if (filters?.products?.length && props.event && !props.event.charges.find((c) => filters?.products.find((fp) => fp.id === c?.product?.id))) {
      return true;
    }

    if (filters?.assetTypes?.length && !props.event?.assets?.some((a) => filters.assetTypes.includes(a.type))) {
      return true;
    }

    return false;
  }

  /**
   * Caches the current date of the schedule in session storage and in the url
   *
   * @param date The date being cached, format: 'YYYY-MM-DD'
   */
  cacheDateAndView(date: string, view: string) {
    const urlView = SCHEDULE_VIEW_TYPE_MAP[view] || SCHEDULE_VIEW_TYPE_MAP.resourceTimelineDay;
    sessionStorage.setItem(environment.lskeys.scheduleDate, date);
    sessionStorage.setItem(environment.lskeys.scheduleView, view);
    this.changeUrl(date, urlView);
  }

  // Calendar is drawn not dynamic, as such for events not watched by FC (container size) we must call this.
  renderAfterDelay() {
    this.calendar.setOption('resourceAreaWidth', this.fcHelper.getResourceAreaWidth());
    // Reduced timeout from 225ms to 50ms to improve perceived performance while still ensuring DOM updates
    const timeout = setTimeout(() => { 
      this.calendar.render();
      this.calendar.updateSize();
      window.dispatchEvent(new Event('resize'));
    }, 50);

    this.timeOuts.push(timeout);
  }

  // QUERY HELPERS
  getScheduleVariables(): { availabilty: AvailabilityQueryVariables; events: ScheduleEventsQueryVariables } {
    const tz = this.getCalendarTimezone();
    // Use activeStart/activeEnd to include padding days (prev/next month days shown in grid).
    // Apply the business timezone so startOf/endOf day boundaries align with the calendar's
    // displayed date rather than the browser's local timezone.
    const start = dayjs(this.calendar.view.activeStart || this.calendar.view.currentStart || new Date()).tz(tz).startOf('day');
    const end = dayjs(this.calendar.view.activeEnd || this.calendar.view.currentEnd || new Date()).tz(tz).endOf('day');

    // add / subtract one period so previous/next loads quickly
    // TODO: REDO this logic to run as supporting requests
    const duration = 0; // end.diff(start, 'day');
    const min = dayjs(start).tz(tz).startOf('day').subtract(duration, 'day').unix();
    const max = dayjs(end).tz(tz).endOf('day').add(duration, 'day').unix();

    return {
      availabilty: {
        objectIds: this.getDisplayedAssets().map((a) => a.id),
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        zone: this.area || undefined,
      },
      events: {
        filter: {
          min,
          max,
          getEventsOfStatus: ['booked', 'confirmed', 'completed'],
        },
        limit: 2000,
      }
    };
  }

  // Updates the event query variables to reflect the calendar date
  async refetchData() {
    this.scheduleQH.loading = true;

    if (this.isMonthView() && !this.showMonthEvents) {
      // Requirement: When in month view and showMonthEvents is false, inject skeleton events (via addSkeletonEvents) within calendar.batchRendering to display aggregated month placeholders while real events load
      this.calendar.batchRendering(() => {
        this.calendar.removeAllEvents();
        this.eventBatches = {};
        this.addSkeletonEvents();
      });
    }

    this.scheduleDebounce.next();
  }

  // ASSETS
  retrieveAssets() {
    if (this.assetsQueryRef) { return; }

    this.assetsQueryRef = this.assetService.watchAssets(
      this.generateAssetInput(),
      { zones: true },
      'cache-and-network' as any,
    );

    this.subs.sink = this.assetsQueryRef.valueChanges.subscribe((res) => {
      // cloneDeep stays: sortAssetsForSchedule sorts the display array in
      // place, so the query result array must not be shared with Apollo.
      const assets = cloneDeep(res?.data?.assets?.assets);
      this.assets = assets || [];

      // cache-and-network replays the cached list before the fresh one;
      // converting resources on every emission doubled the refetchResources
      // render for no gain. (Error emissions with errorPolicy 'all' keep the
      // clear-out behaviour the 7-gate alone would have dropped.)
      if (res.networkStatus !== NetworkStatus.ready && res.networkStatus !== NetworkStatus.error) {
        return;
      }

      this.calendar.batchRendering(() => {
        this.convertAssetsIntoResources();
      });

      if (assets && res.networkStatus === 7) {
        this.refetchData();
      }

      if (!this.assets?.length) {
        this.noAvailableAssetsWarning = 'There are no available assets in the area where current job is being placed.';
      }
    });
  }

  generateAssetInput() {
    return {
      filter: {
        types: this.scheduleFilters.get('assetTypes').value.length ? this.scheduleFilters.get('assetTypes').value : undefined,
        zoneDir: ZoneDir.any,
      },
      limit: -1
    };
  }

  updateAssets() {
    // If the assets have not been set yet return
    if (!this.assets) { return; }
    this.assetsQueryRef.refetch(this.generateAssetInput());
    //console.log(this.assets);
    //console.log(this.generateAssetInput);
  }

  getDisplayedAssets() {
    if (!this.assets) {
      return [];
    }

    let assetsToDisplay = [];
    if (this.scheduleFilters.get('assetTypes').value?.length) {
      assetsToDisplay = (this.assets || []).filter((a) => this.scheduleFilters.get('assetTypes').value.includes(a.type));
    } else {
      assetsToDisplay = this.assets;
    }

    return assetsToDisplay;
  }

  retrieveScheduleData() {
    this.retrieveViewDates();
    this.retrieveProphetMetrics();

    if (!this.isMonthView() || this.showMonthEvents) {
      this.retrieveCalendarEvents();
      this.retrieveAvailability();
    }
  }

  retrieveProphetMetrics() {
    if (!this.showForecast) { return; }
    this.subs.sink = this.prophetMetricsGQL.fetch({}, { fetchPolicy: 'cache-first' })
      .subscribe((res) => {
        this.prophetMetrics = res.data?.prophetMetrics ?? null;
        // The header strip re-publishes (forecast tooltip content changed)
        this.controls.summary.set(this.buildControlsSummary());
      });
  }

  // EVENTS
  retrieveCalendarEvents() {
    const { events: variables } = this.getScheduleVariables();
    if (this.calendarEventQueryRef) {
      // CodeRabbit round 33 (major): an unchanged-variables refetch (same day,
      // same week — e.g. a timeline<->timeGrid switch) can be SUPPRESSED by
      // Apollo: no new valueChanges emission, so the callback that would
      // clear the placeholders installed by the switch never runs. Chain the
      // release onto the refetch promise; the completed-emission processor is
      // guarded by skeletonPlaceholdersInstalled, so a normal (emitting)
      // refetch already released the placeholders and this resolves to a
      // no-op. A refetch that never settles is covered by the bounded
      // fallback timer scheduled at install time.
      this.calendarEventQueryRef.refetch(variables)
        .then((result) => this.releaseStuckSchedulePlaceholders(result))
        .catch(() => undefined);
      return;
    }
    this.calendarEventQueryRef = this.scheduleEventsGQL.watch(variables, {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    });

    this.subs.sink = this.calendarEventQueryRef.valueChanges.subscribe((res) => {
      // Guard: If we are in Month view and NOT showing events, ignore results from old requests
      if (this.isMonthView() && !this.showMonthEvents) {
        return;
      }

      this.scheduleQH.loading = res.loading;
      this.networkLoading = res.loading;

      // Apollo replays cached / intermediate results (previous window, the
      // setVariables replay it used to send on refetch) before the network
      // answer. Process only completed emissions so the ~2k-event pipeline
      // (filter, converts, batch render) runs once per fetch instead of
      // twice-plus on every one (cache-and-network double-emit).
      if (res.networkStatus !== NetworkStatus.ready && res.networkStatus !== NetworkStatus.error) {
        return;
      }

      this.processScheduleEmissionCompleted(res);
    });
  }

  /**
   * Tracks an installed loading-placeholder batch from a view switch
   * (round-33 review). Apollo can suppress the re-emission of an
   * unchanged-variables refetch, so the subscription callback that releases
   * the placeholders is not guaranteed to run — hence the refetch-promise
   * release path and this bounded fallback timer (a refetch stuck here would
   * otherwise keep the "Loading…" board until the next change anywhere on
   * the page).
   */
  /** Bounded fallback for a refetch that never settles (round-33 major). */
  private readonly STUCK_PLACEHOLDER_RELEASE_MS = 30_000;
  private skeletonPlaceholdersInstalled = false;
  private stuckPlaceholderReleaseTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Releases placeholders held past a suppressed refetch. With a settled
   * refetch `result` the completed-emission processor re-runs in full
   * (resources, events, header strip — availability is re-converted from the
   * held data, the condition preserved). Without one (bounded fallback), the
   * board is restored from the component's held state instead.
   */
  private releaseStuckSchedulePlaceholders(result?: ApolloQueryResult<ScheduleEventsQuery>): void {
    if (!this.skeletonPlaceholdersInstalled || this.destroyed) { return; }
    this.skeletonPlaceholdersInstalled = false;
    if (this.stuckPlaceholderReleaseTimer) {
      clearTimeout(this.stuckPlaceholderReleaseTimer);
      this.stuckPlaceholderReleaseTimer = null;
    }
    // Same guard as the valueChanges subscription (CR round 35): the
    // aggregate Month view never renders the day-level event list — the
    // release paths (settled processor or restored-board fallback) would
    // otherwise feed 2000+ events into a month grid after the switch; a
    // destroyed component must not touch a disposed calendar either.
    if (this.isMonthView() && !this.showMonthEvents) { return; }
    if (result?.data) {
      // Same guards the subscription path applies: the processor runs once.
      this.processScheduleEmissionCompleted(result);
    } else {
      this.calendar.batchRendering(() => {
        this.replaceBatch('loading-skeleton', []);
        if (!this.isMonthView() || this.showMonthEvents) {
          this.replaceBatch('view-dates', []);
        }
        this.convertCalendarEventsIntoEvents(this.calendarEvents);
      });
      this.calculateViewTotal();
      this.convertAssetsIntoResources();
    }
    // The availability batch was cleared by the placeholder install; the
    // settled window equals the held data's window (same variables by
    // construction), so re-convert from the held array (the 
    // enableAvailability condition preserved, as the emission path does).
    this.calendar.batchRendering(() => {
      if (this.availabilities && this.enableAvailability) {
        this.convertAvailabilityIntoEvents();
      }
    });
  }

  /** The completed-emission pipeline, shared by the subscription and the
   *  stuck-release path (round-33 major fix: the refetch promise reuses it
   *  when Apollo suppresses the unchanged-variables emission). */
  private processScheduleEmissionCompleted(res: ApolloQueryResult<ScheduleEventsQuery>): void {
    this.skeletonPlaceholdersInstalled = false;
    if (this.stuckPlaceholderReleaseTimer) {
      clearTimeout(this.stuckPlaceholderReleaseTimer);
      this.stuckPlaceholderReleaseTimer = null;
    }

    if (this.reloadClicked && !this.scheduleQH.loading) {
      this.reloadClicked = false;
      // Toast removed (Ray): the bar's refresh button itself shows state
      // (spins + disables until this emission).
      this.refreshDone$.next();
    }

    const allEvents = res.data?.calendarEvents?.events || [];

    // No cloneDeep: the payload is read-only from here on (converted into
    // new FC event objects); Apollo's frozen objects are shared. The old
    // cloneDeep also broke the reference equality the setVariables-skip
    // relied on, and cloning 2k fat events twice per emission was a
    // measured multi-second cost.
    this.calendarEvents = allEvents.filter(
      (e: any) => !e.isDispatchEvent,
    );
    this.calendarEventsById = new Map(this.calendarEvents.map((ce) => [ce.id, ce]));

    // Handle Errors — listIndex counts this response's own event list
    // (dispatch events included), so match against that same list.
    if (!res.loading) {
      this.calendarEventDataErrors = parseGraphqlErrors(res.errors, 'calendarEvents');
      allEvents.forEach((event, index) => {
        const errors = this.calendarEventDataErrors.filter((err) => err.listIndex === index);
        if (errors?.length) {
          this.localNotify.error(
            `Error Occurred on: ${capitalize(event.type)}-${firstInitialLastName(getEventCustomer(event as any)?.user)}`,
            errors.map((err) => err.property).join(', '),
            10000
          );
        }
      });
    }

    this.calendar.batchRendering(() => {
      // Prevent Duplicates & Remove Aggregate View
      // (view-dates live in their own managed source now, so the events
      // refresh no longer needs to strip and re-wait for them.)
      this.eventsInitialized = true;
      this.setCalendarTitle();
      // Loading placeholder bars go the moment the first real emission
      // renders (managed 'loading-skeleton' batch — round 32 nitpick);
      // their resource rows are removed again by
      // convertAssetsIntoResources() below.
      this.replaceBatch('loading-skeleton', []);
      // Month-exit leftovers (incl. direct FullCalendar changeView calls
      // that bypass changeView's month-exit cleanup): the aggregate
      // placeholders are the tracked 'view-dates' batch, so swapping it to
      // [] releases every stale handle. The grid may show REAL events while
      // still a month view — month-with-events (showMonthEvents) skips the
      // aggregate injection and renders the events list, so the placeholder
      // batch would linger next to the real events (CodeRabbit): release it
      // exactly when the aggregate grid is not what is on screen.
      if (!this.isMonthView() || this.showMonthEvents) {
        this.replaceBatch('view-dates', []);
      }
      this.convertCalendarEventsIntoEvents(this.calendarEvents);
    });
    // First completed emission of this switch rendered — the real board
    // replaced the loading placeholders above.
    this.calculateViewTotal();

    this.convertAssetsIntoResources();
  }

  // AVAILABILITY
  retrieveAvailability() {
    // Availability is now controlled by retrieveScheduleData
    // But we still check if we should fetch it (e.g. if we are in month view but showing events, we might want availability? No, month view usually doesn't show availability)
    if (this.isMonthView()) { return; }

    const { availabilty: variables } = this.getScheduleVariables();

    if (this.availabilityQueryRef) {
      this.availabilityQueryRef.refetch(variables);
      return;
    }

    this.availabilityQueryRef = this.availabilityGQL.watch(variables, {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all'
    });

    this.subs.sink = this.availabilityQueryRef.valueChanges.subscribe((res) => {
      // Guard: If we are in Month view, ignore results
      if (this.isMonthView()) { return; }

      this.availabilities = res?.data?.availabilities || [];

      // Process only completed emissions — cache-and-network replays cached
      // results first, and the availability loop adds thousands of
      // inverse-background events when it runs on every emission.
      if (res.networkStatus !== NetworkStatus.ready && res.networkStatus !== NetworkStatus.error) {
        return;
      }

      this.calendar.batchRendering(() => {
        if (this.availabilities && this.enableAvailability) {
          this.convertAvailabilityIntoEvents();
        } else {
          this.availabilities = [];
          this.replaceBatch('availability', []);
        }
      });

    });
  }

  // PARSE OBJECTS TO Full Calendar

  convertCalendarEventsIntoEvents(events: CalendarEventForDispatchFragment[]) {

    const editsAsHolders: FCEventHolder[] = this.latestEventEdits.map((edit) => (
      {
        id: edit.event.id,
        assetIds: edit.event.getResources().map((r) => r.id),
        start: ensureUnixSeconds(edit.event.start.getTime()),
        end: ensureUnixSeconds(edit.event.end.getTime()),
      }
    ));

    // Batch every event into one replaceable batch — per-event removal used
    // to walk FC's store by id (getEventById) for every candidate; removal is
    // now tracked per batch and adds stay in the caller's single
    // batchRendering (see eventBatches note for why not addEventSource).
    const batch: EventInput[] = [];
    const batchIds = new Set<string>();

    for (const ev of events) {
      // Remove book-off events from vertical view
      // Book-off events with no assets are crew member book-offs and should not be shown on the schedule
      if (ev.type === 'book-off' && (this.verticalView || ev.assets.length === 0)) {
        continue;
      }

      let start = ev.start;
      let end = ev.end;

      // This may contain the id of deleted assets...
      const allAssetIds = ev.assets.map((a) => a.id);

      // ...therefore, make sure allAssetIds contains the id of at least one existing asset...
      const hasExistingAssets = ev?.assets?.length
        && intersection(allAssetIds, (this.assets || []).map((a) => a.id)).length;

      // ...otherwise assign 'none' so the event is assigned to the 'Unassigned' resource
      let assetIds = hasExistingAssets ? allAssetIds : ['none'];

      // If we have edits then update the matching events and check for conflicst
      if (this.latestEventEdits?.length) {
        // Handle events that have been edited on the schedule
        const editedEvent = editsAsHolders.find((edit) => edit.id === ev.id);

        if (editedEvent) {
          // Render the edited start instead of the actual start
          start = editedEvent.start;
          end = editedEvent.end;
          assetIds = editedEvent.assetIds;
        }
      }

      let endForRendering = end;

      if ((end - start) < MIN_EVENT_RENDER_TIME) {
        endForRendering += MIN_EVENT_RENDER_TIME - (end - start);
      }

      const {
        backgroundColor,
        textColor,
      } = this.fcHelper.determineColorsFromEvent(ev);

      let editable = this.editModeEnabled;

      if (this.freyaHelper.lockDate > ev.end) {
        editable = false;
      }

      if (ev.invoices?.some(isFinalizedInvoice)) {
        editable = false;
      }

      // Create Actual Event
      const event: EventInput = {
        title: ev.title,
        groupId: ev.id,
        start: start * 1000,
        end: endForRendering * 1000,
        id: ev.id,
        extendedProps: {
          type: 'calendar-event',
          eventId: ev.id,
          event: ev,
          eventType: ev.type,
        },
        resourceIds: assetIds,
        color: backgroundColor,
        textColor,
        editable,
        resourceEditable: editable,
      };
      batch.push(event);
      batchIds.add(event.id);

    }

    // Add any events that were edited here but didn't originate here
    for (const edit of this.latestEventEdits) {
      // Convert the dates to the correct timezone
      const calendarDateInTZ = dayjs(this.calendar.getDate());

      const {
        backgroundColor,
        textColor,
      } = this.fcHelper.determineColorsFromEvent(edit.event.extendedProps?.event);

      // The batch isn't in the calendar yet (per-event adds land at the end —
      // that's the point), so check its ids instead of getEventById.
      if (calendarDateInTZ.date() === edit.event.start.getDate() && !batchIds.has(edit.event.id)) {
        const event = {
          title: edit.event.title,
          groupId: edit.event.id,
          start: ensureUnixSeconds(edit.event.start.getTime() / 1000) * 1000,
          end: ensureUnixSeconds(edit.event.end.getTime() / 1000) * 1000,
          id: edit.event.id,
          extendedProps: {
            type: 'calendar-event',
            eventId: edit.event.id,
            event: edit.event.extendedProps?.event,
            eventType: edit.event.extendedProps?.type,
            loaded: false,
          },
          resourceIds: edit.event.extendedProps?.event.assets.map((r) => r.id),
          color: backgroundColor,
          textColor,
        };

        batch.push(event);
      }
    }

    this.replaceBatch('calendar-events', batch);

    // After all events have been looped the conflicts list will be up to date
    this.checkingForConflicts = false;
    this.renderAfterDelay();
  }

  checkForConflicts() {

    let count = 0;

    const eventsAsHolders: FCEventHolder[] = this.calendarEvents.map((e) => (
      {
        id: e.id,
        assetIds: e.assets.map((a) => a.id),
        start: e.start,
        end: e.end,
      }
    ));

    const editsAsHolders: FCEventHolder[] = this.latestEventEdits.map((edit) => (
      {
        id: edit.event.id,
        assetIds: edit.event.getResources().map((r) => r.id),
        start: ensureUnixSeconds(edit.event.start.getTime()),
        end: ensureUnixSeconds(edit.event.end.getTime()),
      }
    ));

    for (const ev of this.calendarEvents) {

      const holder = editsAsHolders.find((edit) => edit.id === ev.id);

      const editedEvent = this.latestEventEdits.find((edit) => edit.event.id === ev.id);

      if (holder) {
        if (this.fcHelper.conflictsWithEvent(eventsAsHolders, {
          id: ev.id,
          start: holder.start,
          end: holder.end,
          assetIds: holder.assetIds,
        }, editsAsHolders)) {
          editedEvent.hasConflict = true;
          count++;
        } else {
          editedEvent.hasConflict = false;
        }
      }
    }

    this.eventsWithConflictsCount = count;
  }

  // Turn System Assets into Calendar Resources
  convertAssetsIntoResources() {
    this.calendar.batchRendering(() => {
      this.calendar.refetchResources();
      if (!this.assets) { return; }

      // Loading placeholder rows are REAL resources since the Ray skeleton
      // batch (R2) — remove them here so the real conversion starts clean
      // even if the first emission's event sweep already ran.
      this.calendar.getResources()
        .filter((r) => r.id.startsWith('skeleton-loading-'))
        .forEach((r) => r.remove());
      let assetsToDisplay = this.getDisplayedAssets();

      if (this.scheduleFilters.get('assetTypes').value?.length) {
        assetsToDisplay = (this.assets || []).filter((a) => this.scheduleFilters.get('assetTypes').value.includes(a.type));
      } else {
        assetsToDisplay = this.assets;
      }

      // Sort asset array by type, then name
      sortAssetsForSchedule(assetsToDisplay);

      let order = 0;

      const viewStart = this.calendar.view.currentStart.getTime() / 1000;
      const viewEnd = this.calendar.view.currentEnd.getTime() / 1000;

      // Requirement: Filter based on createdAt and deletedAt to show only active resources for the period.
      // - Show resources created before the view ends.
      // - Show resources deleted after the view starts (or not deleted at all).
      // Requirement: Exception to the above is if the resource is currently in use by an event in the current view.
      // Even if an asset is technically "deleted" or "not created yet" relative to the view (edge cases),
      // if it has an event assigned, we must display it to avoid orphaned events.
      const activeAssetIds = new Set<string>();
      if (this.calendarEvents) {
        this.calendarEvents.forEach(ev => {
          if (ev.start < viewEnd && ev.end > viewStart) {
            ev.assets.forEach(a => activeAssetIds.add(a.id));
          }
        });
      }

      assetsToDisplay = assetsToDisplay.filter(asset => {
        // If asset is in use in the current view, always show it
        if (activeAssetIds.has(asset.id)) {
          return true;
        }

        // Ensure we parse dates to unix seconds if they are ISO strings, or assume they are compatible if not
        // Assets typically have createdAt/deletedAt as strings or Date objects depending on the GQL query.
        // Assuming asset properties are available as strings or dates.
        const createdAt = ensureUnixSeconds(new Date(asset.createdAt).getTime());
        const deletedAt = asset.deletedAt ? ensureUnixSeconds(new Date(asset.deletedAt).getTime()) : null;

        // Created before the view ends
        if (createdAt >= viewEnd) {
          return false;
        }

        // Deleted after the view starts (or not deleted)
        if (deletedAt && deletedAt <= viewStart) {
          return false;
        }

        return true;
      });

      // Calculate Common Crew (Intersection) and All Day Crew (Union) for Unassigned
      let unassignedUnionIds: string[] = [];
      let unassignedIntersectionIds: string[] | null = null;
      
      let unassignedUnionDetails: any[] = [];
      let unassignedIntersectionDetails: any[] = [];

      // Only calculate crew stats for resources in timeline views, not in month view
      if (this.calendarEvents && !this.verticalView && !this.isMonthView()) {
        const allAssetIds = new Set((this.assets || []).map(a => a.id));
        const unassignedEvents = this.calendarEvents.filter(ev => {
          const evAssetIds = ev.assets.map(a => a.id);
          const hasExistingAssets = evAssetIds.some(id => allAssetIds.has(id));
          return !hasExistingAssets && ev.type !== 'book-off' && ev.start < viewEnd && ev.end > viewStart;
        });

        if (unassignedEvents.length > 0) {
          const firstEventCrew = unassignedEvents[0].attendees
            ?.filter(att => ['Crew Lead', 'Assistant Crew Lead', 'Crew Member', 'Driver'].includes(att.role))
            .map(att => att.user.id) || [];
          
          unassignedUnionIds = [...firstEventCrew];
          unassignedIntersectionIds = [...firstEventCrew];

          for (let i = 1; i < unassignedEvents.length; i++) {
            const eventCrew = unassignedEvents[i].attendees
              ?.filter(att => ['Crew Lead', 'Assistant Crew Lead', 'Crew Member', 'Driver'].includes(att.role))
              .map(att => att.user.id) || [];
            
            unassignedUnionIds = union(unassignedUnionIds, eventCrew);
            unassignedIntersectionIds = intersection(unassignedIntersectionIds, eventCrew);
          }
          
          // Map IDs to Details
          const allAttendees = unassignedEvents.flatMap(ev => ev.attendees || []);
          
          unassignedUnionDetails = unassignedUnionIds.map(id => {
             const attendee = allAttendees.find(a => a.user.id === id);
             return attendee ? { ...attendee } : null;
          }).filter(Boolean);

          if (unassignedIntersectionIds) {
            unassignedIntersectionDetails = unassignedIntersectionIds.map(id => {
                const attendee = allAttendees.find(a => a.user.id === id);
                return attendee ? { ...attendee } : null;
             }).filter(Boolean);
          }
        }
      }

      this.calendar.addResource({ // Add a resource for events that don't have any assets
        id: 'none',
        title: 'Unassigned',
        eventColor: 'red',
        type: 'Unassigned',
        order,
        extendedProps: {
          allDayCrew: unassignedUnionDetails,
          commonCrew: unassignedIntersectionDetails
        }
      });
      order++;

      // Filter Hide Unused
      if (this.hideUnused) {
        assetsToDisplay = assetsToDisplay.filter(asset => {
          if (!this.eventsInitialized) {
            return true;
          }

          // Find events for this asset that overlap with the current view
          const assetEvents = this.calendarEvents.filter(ev =>
            ev.assets.some(a => a.id === asset.id) &&
            ev.start < viewEnd && ev.end > viewStart
          );

          // An asset is unused if it has no events, or if all its events are book-offs
          const hasNonBookOffEvents = assetEvents.some(ev => ev.type !== 'book-off');
          return hasNonBookOffEvents;
        });
      }

      // Filter Hide Unavailable
      if (this.hideUnavailable) {
        assetsToDisplay = assetsToDisplay.filter(asset => {
          if (!this.eventsInitialized) {
            return true;
          }

          // If the asset has any events (job, estimate, etc) show it regardless of availability
          const hasEvents = this.calendarEvents.some(ev =>
            ev.type !== 'book-off' &&
            ev.assets.some(a => a.id === asset.id) &&
            ev.start < viewEnd && ev.end > viewStart
          );

          if (hasEvents) {
            return true;
          }

          // Check Book Off
          // An asset is unavailable if the start / end of the book off is within 3 hours of the start/end of the day
          const threeHours = 3 * 60 * 60;
          const isFullyBookedOff = this.calendarEvents.some(ev =>
            ev.type === 'book-off' &&
            ev.assets.some(a => a.id === asset.id) &&
            ev.start <= viewStart + threeHours && ev.end >= viewEnd - threeHours
          );

          if (isFullyBookedOff) return false;

          // Check Availability
          if (!this.availabilities) {
            return true;
          }

          const avail = this.availabilities.find(av => av.objectId === asset.id);
          // If no availability record or no slots in range, consider unavailable
          if (!avail) return false;

          const hasSlots = avail.dayAvailability.some(d => d.availability && d.availability.length > 0);
          if (!hasSlots) return false;

          return true;
        });
      }

      for (const asset of assetsToDisplay) {
        // Calculate Common Crew (Intersection) and All Day Crew (Union)
        let unionIds: string[] = [];
        let intersectionIds: string[] | null = null;
        
        let unionDetails: any[] = [];
        let intersectionDetails: any[] = [];

        // Only calculate crew stats for resources in timeline views, not in month view
        if (this.calendarEvents && !this.verticalView && !this.isMonthView()) {
          const relevantEvents = this.calendarEvents.filter(ev =>
            ev.type !== 'book-off' &&
            ev.assets.some(a => a.id === asset.id) &&
            ev.start < viewEnd && ev.end > viewStart
          );

          if (relevantEvents.length > 0) {
            const firstEventCrew = relevantEvents[0].attendees
              ?.filter(att => ['Crew Lead', 'Assistant Crew Lead', 'Crew Member', 'Driver'].includes(att.role))
              .map(att => att.user.id) || [];
            
            unionIds = [...firstEventCrew];
            intersectionIds = [...firstEventCrew];

            for (let i = 1; i < relevantEvents.length; i++) {
              const eventCrew = relevantEvents[i].attendees
                ?.filter(att => ['Crew Lead', 'Assistant Crew Lead', 'Crew Member', 'Driver'].includes(att.role))
                .map(att => att.user.id) || [];
              
              unionIds = union(unionIds, eventCrew);
              intersectionIds = intersection(intersectionIds, eventCrew);
            }

            const allAttendees = relevantEvents.flatMap(ev => ev.attendees || []);
            
            unionDetails = unionIds.map(id => {
                const attendee = allAttendees.find(a => a.user.id === id);
                return attendee ? { ...attendee } : null;
            }).filter(Boolean);

            if (intersectionIds) {
                intersectionDetails = intersectionIds.map(id => {
                    const attendee = allAttendees.find(a => a.user.id === id);
                    return attendee ? { ...attendee } : null;
                }).filter(Boolean);
            }
          }
        }

        this.calendar.addResource({
          id: asset.id,
          title: formatCalendarAssetTitle(asset.name, asset.type, asset.description),
          eventColor: asset.zones[0]?.color,
          extendedProps: {
            id: asset.id,
            type: asset.type,
            name: asset.name,
            allDayCrew: unionDetails,
            commonCrew: intersectionDetails
          },
          order,
        });

        // increment order
        order++;
      }

      const loadingResource = this.calendar.getResourceById('loading');

      loadingResource?.remove();
    });

    if (this.dynamicHeight) {
      this.setCalendarHeight('auto');
    }

    this.renderAfterDelay();
  }

  convertAvailabilityIntoEvents() {
    // Swaps the availability batch in place instead of per-event removeEvents
    // (one batchRendering call site upstream); a vertical view now also ends
    // with the availability batch cleared — same net effect as the
    // removeEventsOfTypes(['availability']) call that preceded this.
    const batch: EventInput[] = [];

    if (!this.verticalView) {
      for (const object of this.availabilities) {

        for (const day of object.dayAvailability) {

          // If the asset has no availability, we need to put a dummy availability to show the inverse of
          if (!day.availability?.length) {
            batch.push({
              title: 'Unavailable',
              groupId: `${object.objectId}-availability`,
              start: `${day.date}T00:00:00`,
              end: `${day.date}T00:00:1`,
              display: 'inverse-background',
              // The Asset that this availability belongs to
              resourceIds: [object.objectId],
              color: '#666666',
              extendedProps: {
                type: 'availability',
              },
            });

            continue;
          }

          for (const avail of day.availability) {
            const start = avail.start * 1000;
            const end = avail.end * 1000;

            batch.push({
              title: 'Unavailable',
              groupId: `${object.objectId}-availability`,
              start,
              end,
              display: 'inverse-background',
              // The Asset that this availability belongs to
              resourceIds: [object.objectId],
              color: '#666666',
              extendedProps: {
                type: 'availability',
              },
            });
          }
        }
      }
    }

    this.replaceBatch('availability', batch);
  }

  retrieveViewDates() {
    let startDate: string;
    let endDate: string;

    // Requirement: In month view, retrieve full visible range including padding days
    if (this.isMonthView() && this.calendar?.view?.activeStart) {
      startDate = dayjs(this.calendar.view.activeStart).format('YYYY-MM-DD');
      endDate = dayjs(this.calendar.view.activeEnd).subtract(1, 'day').format('YYYY-MM-DD');
    } else {
      // We want viewDates for the whole month to support highlighting, regardless of view
      const currentDate = dayjs(this.calendar.getDate());
      startDate = currentDate.startOf('month').format('YYYY-MM-DD');
      endDate = currentDate.endOf('month').format('YYYY-MM-DD');
    }

    const variables = {
      startDate,
      endDate,
      zone: this.area || undefined,
      includeEvents: false,
      spreadRevenue: this.spreadRevenue,
      prophet: this.showForecast,
      showPacing: this.showPacing,
    };

    if (this.viewDatesQueryRef) {
      this.viewDatesQueryRef.refetch(variables);
      return;
    }

    this.viewDatesQueryRef = this.viewDatesGQL.watch(variables, {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    });

    this.subs.sink = this.viewDatesQueryRef.valueChanges.subscribe((res) => {
      // If loading and no data, keep existing skeletons/data
      if (res.loading && !res.data?.viewDates) {
        return;
      }

      // No cloneDeep: viewDatesData is only read (filter/find/forEach); the
      // day objects are embedded read-only in view-dates event extendedProps.
      const viewDates = res.data?.viewDates?.filter((d): d is NonNullable<typeof d> => d != null);
      this.viewDatesData = viewDates || [];

      // Month aggregate view skips the calendar-events query entirely, so
      // it is the only shown completion of a bar-initiated refresh — signal
      // one here (CodeRabbit; gate released on whatever the query returns,
      // including errors, so the button can't stick).
      if (this.isMonthView() && !this.showMonthEvents && this.reloadClicked) {
        this.reloadClicked = false;
        this.refreshDone$.next();
      }
      this.scheduleQH.loading = res.loading;

      this.eventsInitialized = true;

      // Process only completed emissions — cache-and-network replays cached
      // results first, so the aggregate swap, highlight math, remarks and
      // totals ran twice on every day/week entry.
      if (res.networkStatus !== NetworkStatus.ready && res.networkStatus !== NetworkStatus.error) {
        return;
      }

      this.updateDateRemarks();

      // Only render aggregate events if in correct mode
      if (this.isMonthView() && !this.showMonthEvents) {
        this.calendar.batchRendering(() => {
          // Remove all other events when showing aggregate view
          this.calendar.removeAllEvents();
          this.eventBatches = {};
          if (this.viewDatesData) {
            this.convertViewDatesToEvents(this.viewDatesData);
          }
        });
      }

      this.calculateViewTotal();
      setTimeout(() => this.updateHeaderHeight(), 50);
    });
  }

  // Helper methods for template (Day View Stats)
  getEventTypeColor(type: string): string {
    const backgroundColor = eventTypeInfoMapV2[type]?.backgroundColor || 'primary-color';
    return `var(--${backgroundColor})`;
  }

  get truckUtilization(): number {
    if (!this.currentDayViewStats?.truckCapacity) return 0;
    const used = this.currentDayViewStats.actualTrucksUsed;
    if (used <= 0) return 0; // Ensure 0% if used is 0
    return Math.min(100, (used / this.currentDayViewStats.truckCapacity) * 100);
  }

  get crewUtilization(): number {
    if (!this.currentDayViewStats?.crewCapacity) return 0;
    const used = this.currentDayViewStats.actualCrewUsed;
    if (used <= 0) return 0; // Ensure 0% if used is 0
    return Math.min(100, (used / this.currentDayViewStats.crewCapacity) * 100);
  }

  get maxSystemUtilization(): number {
    return Math.max(this.truckUtilization, this.crewUtilization);
  }

  convertViewDatesToEvents(data: NonNullable<ViewDatesQuery['viewDates']>) {
    const filteredData = data.filter((d): d is NonNullable<typeof d> => d != null);
    const batch: EventInput[] = [];
    for (const dayData of filteredData) {
      batch.push({
        title: ' ',
        start: dayData.date,
        allDay: true,
        display: 'block',
        classNames: ['view-dates-event'],
        extendedProps: {
          type: 'view-dates',
          data: dayData,
        },
      });
    }
    // Month aggregate view: swaps the aggregate batch in place.
    this.replaceBatch('view-dates', batch);
  }

  /** Install the loading board on the REAL calendar (Ray: use actual
   *  FullCalendar resources for the loaders instead of a separate skeleton
   *  overlay div). Resource timelines: several "Loading…" resource rows,
   *  each with a couple of shimmer event bars; vertical timeGrid: the bars
   *  only (no resource axis). Stale events AND stale resource names are
   *  replaced entirely so the old board's data never flashes through. The
   *  first completed emission clears the bars (managed 'loading-skeleton'
   *  batch) and convertAssetsIntoResources() strips the placeholder rows,
   *  so both halves converge. */
  /** The placeholder row's label DOM — mirrors the real
   *  schedule-resource-label shape (inline-flex column, 44px, bold title)
   *  without instantiating the Angular component (it needs a real asset
   *  id). Styles live in schedules.component.scss under the
   *  td[data-resource-id^='skeleton-loading-'] rules (Ray: same height and
   *  font as the others so the loaded board doesn't shift). */
  private static buildSkeletonLabel(title: string): { domNodes: HTMLElement[] } {
    const container = document.createElement('div');
    container.className = 'resource-label-container';
    const row = document.createElement('div');
    row.className = 'title-row';
    const label = document.createElement('span');
    label.className = 'resource-title';
    label.textContent = title;
    row.appendChild(label);
    container.appendChild(row);
    return { domNodes: [container] };
  }

  private installLoadingPlaceholders(viewType: string) {
    const timeline = viewType === 'resourceTimelineDay' || viewType === 'resourceTimelineWeek';
    // The switch's refetch may never emit (round-33 major: Apollo suppresses
    // unchanged-variables refetches). Schedule the bounded fallback release
    // now; the completed emission or the refetch promise clears it sooner.
    this.skeletonPlaceholdersInstalled = true;
    if (this.stuckPlaceholderReleaseTimer) { clearTimeout(this.stuckPlaceholderReleaseTimer); }
    this.stuckPlaceholderReleaseTimer = setTimeout(() => {
      this.stuckPlaceholderReleaseTimer = null;
      this.releaseStuckSchedulePlaceholders();
    }, this.STUCK_PLACEHOLDER_RELEASE_MS);
    this.calendar.batchRendering(() => {
      // Stale events of the previous view must not flash through the
      // placeholder rows (the removed overlay used to hide them).
      this.calendar.removeAllEvents();
      this.eventBatches = {};
      if (timeline) {
        // Replace the whole resource list (stale asset names should not
        // stay on the board while it claims to be loading).
        const resources: EventInput[] = [];
        for (let i = 1; i <= this.LOADING_RESOURCE_COUNT; i++) {
          const id = `skeleton-loading-${i}`;
          resources.push({ id, title: 'Loading…', extendedProps: { skeletonResource: true } });
        }
        this.calendar.setOption('resources', resources as any);
      }

      // Skeleton event bars, staggered like a real day's load. They are
      // ordinary calendar events (so they scroll/paginate with the window),
      // tracked as a managed batch: the first real emission clears it via
      // replaceBatch — no full-store scan (CodeRabbit round 32).
      const bars: EventInput[] = [];
      const viewStart = dayjs(this.calendar.view.currentStart);
      const spanHours = timeline ? 13 : 9;
      for (let i = 1; i <= (timeline ? this.LOADING_RESOURCE_COUNT : 6); i++) {
        const start = viewStart.add(6 + ((i * 3) % spanHours), 'hour');
        const end = start.add(2 + ((i * 5) % 4), 'hour');
        const bar: EventInput = {
          id: `skeleton-loading-event-${i}`,
          title: ' ',
          start: start.toDate(),
          end: end.toDate(),
          classNames: ['skel-loading-event'],
          extendedProps: { loadingSkeleton: true },
        } as any;
        if (timeline) {
          (bar as any).resourceId = `skeleton-loading-${i}`;
        }
        bars.push(bar);
      }
      this.replaceBatch('loading-skeleton', bars);
    });
  }

  addSkeletonEvents(startInput?: any, endInput?: any) {
    const start = startInput ? dayjs(startInput) : dayjs(this.calendar.view.activeStart || this.calendar.view.currentStart);
    const end = endInput ? dayjs(endInput) : dayjs(this.calendar.view.activeEnd || this.calendar.view.currentEnd);

    const skeletons: EventInput[] = [];
    let current = start;
    while (current.isBefore(end)) {
      skeletons.push({
        title: ' ',
        start: current.format('YYYY-MM-DD'),
        allDay: true,
        display: 'block',
        classNames: ['view-dates-event'],
        extendedProps: {
          type: 'view-dates',
          data: null,
        },
      });
      current = current.add(1, 'day');
    }
    // Swaps out the previous view-dates batch (dedupe).
    this.replaceBatch('view-dates', skeletons);
  }

  // NOT CURRENTLY IN USE, REVISED VERSION TODO
  // selectAvailability(offsetX: number, width: number, resourceId: string, availability: Availability[]) {
  //   const numberOfAvails = availability.length;

  //   // Convert the Conainer width/offset to a position in the array
  //   let index = Math.round(offsetX / width * numberOfAvails);
  //   if (index >= numberOfAvails) {
  //     index -= 1;
  //   }

  //   // Notify any components listening that we clicked on an availability
  //   this.fcHelper.availabilityClicked.next({
  //     assetId: resourceId,
  //     date: this.calendar.getDate(),
  //     time: availability[index].start,
  //   } as BookingInformation);
  // }


  // EDITING
  /**
   * Enables edited on the FullCalendar scheduler and sets the values used by other functions to modify functionality while in edit mode.
   *
   * @param enabled Sets values to match, If true we are enabling, if false we are disabling
   */
  enableEditMode(enabled = true) {
    // If there is no change to editModeEnabled
    if (this.editModeEnabled === enabled) { return; }

    // Month view is read-only (#7275): the aggregate grid renders from
    // skeleton events and the re-add paths skip the month view, so entering
    // edit mode here would blank the board.
    if (enabled && this.isMonthView()) { return; }

    this.eventEdits = [];
    this.latestEventEdits = [];

    this.editModeEnabled = enabled;

    this.freyaHelper.scheduleEditModeEnabled.next(enabled);

    if (!enabled) {
      this.discardEditsDialogVisible = false;
    }

    // Reload events
    this.calendar.batchRendering(() => {
      this.calendar.setOption('editable', enabled);
      this.calendar.setOption('droppable', enabled);
      this.calendar.setOption('eventDurationEditable', enabled);

      this.calendar.removeAllEvents();
      this.eventBatches = {};
      this.convertAvailabilityIntoEvents();
      this.convertCalendarEventsIntoEvents(this.calendarEvents);
    });
  }

  /**
   * Tracks a change that the user makes while editing
   */
  logNewEdit(edit: EventChange) {
    if (this.latestEventEdits?.length >= this.maximumEditableEvents && !this.latestEventEdits.find((le) => le.event.id === edit.event.id)) {
      edit.revert();
      this.localNotify.warning('Maximum Events Reached', 'You have reached the maximum number of editable events.');
      return;
    }
    // Redo history is removed after adding a new change
    this.undoneEventEdits = [];

    // Push the latest edit
    this.eventEdits.push(edit);
    this.setLatestEventEdits();
  }

  /**
   * Undoes the last edit in the list
   */
  undoLastEdit() {
    if (!this.eventEdits.length) { return; }

    // Revert the edit
    const lastEdit = this.eventEdits[this.eventEdits.length - 1];

    const fullCalendarEvent = this.calendar.getEventById(lastEdit.event.id);

    fullCalendarEvent.setStart(lastEdit.oldEvent.start);
    fullCalendarEvent.setEnd(lastEdit.oldEvent.end);
    fullCalendarEvent.setResources(lastEdit.oldEvent.getResources());

    // Remove the edit from the list
    this.eventEdits.splice(this.eventEdits.length - 1, 1);

    // Add the edit to list of undone edits
    this.undoneEventEdits.push(lastEdit);

    this.setLatestEventEdits();
    this.checkForConflicts();
  }

  /**
   * Redo the latest edit that was undone
   */
  redoEdit() {
    if (!this.undoneEventEdits?.length) { return; }

    const editToRedo = this.undoneEventEdits[this.undoneEventEdits.length - 1];

    const fullCalendarEvent = this.calendar.getEventById(editToRedo.event.id);

    fullCalendarEvent.setStart(editToRedo.event.start);
    fullCalendarEvent.setEnd(editToRedo.event.end);
    fullCalendarEvent.setResources(editToRedo.event.getResources());

    // Remove the edit from the list
    this.undoneEventEdits.splice(this.undoneEventEdits.length - 1, 1);

    // Relog this edit without clearing the edit list
    this.eventEdits.push(editToRedo);
    this.setLatestEventEdits();
    this.checkForConflicts();

  }

  /**
   * Updates the list of the most recent event
   */
  setLatestEventEdits() {
    const finalEdits: EventChange[] = [];
    // Loop through the list of changes backwards to get the most recent change first
    for (let index = this.eventEdits.length - 1; index >= 0; index--) {
      // We already have a more up to date version of this event
      if (finalEdits.find((fe) => fe.event.id === this.eventEdits[index].event.id)) {
        continue;
      }

      finalEdits.push(this.eventEdits[index]);
    }

    this.latestEventEdits = finalEdits;

    // Updates the calendar so newly edited items have and outline
    this.calendar.render();
  }

  showDiscardDialog() {
    // Just disable edit mode if there are no outstanding changes
    if (!this.latestEventEdits?.filter((e) => e.status !== 'Saved')?.length) {
      this.enableEditMode(false);
      return;
    }
    this.discardEditsDialogVisible = true;
  }

  discardEdit(edit: EventChange) {
    this.latestEventEdits = this.latestEventEdits.filter((e) => e.event.id !== edit.event.id);
    this.eventEdits = this.eventEdits.filter((e) => e.event.id !== edit.event.id);
  }

  /**
   * Discard any existing edits and exit edit mode
   */
  discardEdits() {
    this.enableEditMode(false);
    this.notifyAttendees = true;
    this.refetchData();
    this.discardEditsDialogVisible = false;
    this.localNotify.success('Event edits discarded');
  }

  /**
   * Save if there are no conflicts, prompt the user if there are
   */
  tryToSave() {
    if (this.isSaving) return;

    if (!this.latestEventEdits.filter((e) => e.hasConflict)?.length) {
      this.saveAllEdits();
      return;
    }

    this.confirmationService.confirm({
      header: 'Conflicts found',
      message: 'One or more of the events you are trying to save have a conflict, are you sure you want to continue?',
      acceptLabel: 'Continue',
      accept: () => {
        this.saveAllEdits();
      }
    });
  }

  /**
   * Prepare the observable input for a batch edit
   *
   * @param edit The edit to save
   * @returns Observable of the edit query
   */
  getEditInput(edit: EventChange) : SingleEditInput {
    const { added, removed } = this.freyaHelper.getAddedAndRemoved(
      edit.event?.extendedProps?.event.assets.map((a) => a.id),
      edit.event.getResources().map((r) => r.id),
      false
    );

    const editInput: EditCalendarEventInput = {};

    if (edit.event.start.getTime() !== edit.oldEvent.start.getTime()) {
      editInput.start = ensureUnixSeconds(edit.event.start.getTime());
    }

    if (edit.event.end.getTime() !== edit.oldEvent.end.getTime()) {
      editInput.end = ensureUnixSeconds(edit.event.end.getTime());
    }

    if (added?.length) {
      editInput.setAssets = editInput.setAssets || {};
      editInput.setAssets.addAssets = added;
    }

    if (removed?.length) {
      editInput.setAssets = editInput.setAssets || {};
      editInput.setAssets.removeAssets = removed;
    }

    return {
      id: edit.event.id,
      edit: editInput,
      notifyAttendees: this.notifyAttendees,
    };
  }

  notifyAttendeesChecked(event?: { checked?: boolean }) {
    this.notifyAttendees = Boolean(event?.checked);
  }

  /**
   * Loops through and saves all edits that have not already been saved
   */
  saveAllEdits() {
    this.isSaving = true;
    // Filter out edits that are already saved
    const unsavedEdits = this.latestEventEdits.filter(edit => edit.status !== 'Saved');
    if (!unsavedEdits.length) {
      this.isSaving = false;
      return;
    }

    // Prepare bulk edit input for GraphQL mutation
    const bulkEditInputs: SingleEditInput[] = unsavedEdits.map(edit => {
        edit.status = 'Saving';
        return this.getEditInput(edit);
    });

    // Save all edits using bulk mutation
    this.subs.sink = this.bulkEditCalendarEventGQL.mutate({ edits: bulkEditInputs })
      .subscribe({
        next: (res) => {
          // On success: mark all edits as saved
          unsavedEdits.forEach(edit => { edit.status = 'Saved'; });
          this.localNotify.success(`${unsavedEdits.length} event${unsavedEdits.length > 1 ? 's' : ''} saved`);
          this.isSaving = false;
          this.notifyAttendees = true;

          // Requirement: Patch the saved start/end/assets into calendarEvents so enableEditMode(false)
          // doesn't redraw a moved/resized event from its stale pre-save position
          const savedEvents = res.data?.bulkEditCalendarEvent?.events;
          if (savedEvents?.length) {
            this.calendarEvents = this.calendarEvents.map((ce) => {
              const saved = savedEvents.find((se) => se.id === ce.id);
              return saved ? { ...ce, start: saved.start, end: saved.end, assets: saved.assets } : ce;
            });
            this.calendarEventsById = new Map(this.calendarEvents.map((ce) => [ce.id, ce]));
          }

          // Resource visibility (hideUnavailable) is derived from calendarEvents (book-off coverage),
          // so it must be rebuilt now that calendarEvents reflects the saved start/end/assets.
          this.convertAssetsIntoResources();

          this.enableEditMode(false);
          // Requirement: refetch availability for cases when event was rescheduled to another asset
          // or duration decreased and time slot became available
          this.retrieveAvailability();
        },
        error: (error) => {
          // Handle error: mark all edits as failed and notify user
          this.localNotify.apolloError('Failed to save changes', error);
          unsavedEdits.forEach(edit => { edit.status = 'Failed'; });
          this.isSaving = false;
          this.notifyAttendees = true;
          return of(error);
        },
      });
}

  setMaxEvents(showAllEvents: boolean) {
    this.showMonthEvents = showAllEvents;
    // When showing individual events in month view, limit events per day to prevent browser crash
    // with large event volumes. FullCalendar will show a "+X more" popover for the rest.
    this.calendar.setOption('dayMaxEvents', showAllEvents ? true : false);
    this.updateToggleState('showMonthEvents', showAllEvents);
    this.updateMenu();
    this.refetchData();
  }

  toggleShowCrew() {
    this.showCrew = !this.showCrew;
    this.updateToggleState('showCrew', this.showCrew);
    this.convertAssetsIntoResources();
    this.calendar.render();
    this.updateMenu();
  }

  private updateMenu() {
    this.menuOptions = [
      {
        label: 'Vertical View',
        icon: this.verticalView ? 'pi pi-check' : 'pi pi-fw',
        command: () => this.toggleVerticalView(),
        visible: !this.isMonthView()
      },
      {
        label: 'Hide Unavailable',
        icon: this.hideUnavailable ? 'pi pi-check' : 'pi pi-fw',
        command: () => this.toggleHideUnavailable()
      },
      {
        label: 'Hide Unused',
        icon: this.hideUnused ? 'pi pi-check' : 'pi pi-fw',
        command: () => this.toggleHideUnused()
      },
      {
        label: 'Show All Day',
        icon: this.showAllDay ? 'pi pi-check' : 'pi pi-fw',
        command: () => this.toggleShowAllDay()
      },
      {
        label: 'Show Crew',
        icon: this.showCrew ? 'pi pi-check' : 'pi pi-fw',
        command: () => this.toggleShowCrew()
      },
      {
        label: 'Spread Revenue',
        icon: this.spreadRevenue ? 'pi pi-check' : 'pi pi-fw',
        command: () => this.toggleSpreadRevenue(),
      },
      {
        label: 'Show Forecast',
        icon: this.showForecast ? 'pi pi-check' : 'pi pi-fw',
        command: () => this.toggleShowForecast(),
      },
      {
        label: 'Show Pacing',
        icon: this.showPacing ? 'pi pi-check' : 'pi pi-fw',
        command: () => this.toggleShowPacing(),
        style: { color: 'var(--green-500)' }
      },
      {
        label: 'Open in dynamic reports',
        icon: 'pi pi-chart-bar',
        command: () => this.openDynamicReports()
      },
      {
        label: 'Copy for spreadsheet',
        icon: 'pi pi-table',
        command: () => this.copyScheduleHtml()
      },
      {
        label: 'Export',
        icon: 'pi pi-calendar-plus',
        visible: !this.editModeEnabled && this.canExportIcal,
        command: () => this.openIcalDialog()
      },
      {
        label: 'Calendar Remarks',
        icon: 'pi pi-comment',
        visible: this.canSetConfig,
        command: () => this.openCalendarRemarksDialog()
      }
    ];

    // Register the shared controls-bar config (fresh menuOptions + handlers);
    // updateMenu() runs on init and every date/view change, so the bar's
    // gear/refresh/pencil stay current. Deferred: datesSet fires synchronously
    // inside this component's controlsDateEffect (FullCalendar's sync React
    // flush), and signal writes are not allowed in a reactive context.
    this.controlsConfig = {
      // Ray's window semantics per view: the week tab records WHOLE weeks
      // (Mon–Sun, FC firstDay 1) — range-mode window carrying + the 'week'
      // preset granularity for stepping/Today so < > and Today keep Mon–Sun
      // spans; one crumb click selects the displayed week. Day/month tabs
      // keep single-date semantics (day mode clears a stray end).
      mode: this.isWeekView() ? 'range' : 'day',
      rangeGranularity: this.isWeekView() ? 'week' : undefined,
      // Follow the displayed view's scale so the bar steps by the grid:
      // week by weeks (Mon presets), month by month, day by day (CodeRabbit).
      navScale: this.isMonthView() ? 'month' : (this.isWeekView() ? 'week' : 'day'),
      // Month view is read-only (#7275): no pencil on the aggregate grid —
      // edit mode there wipes the board (removeAllEvents + month-skip re-add).
      canEdit: this.canEditSchedule && !this.isMonthView(),
      gearMenu: this.menuOptions,
      onEdit: () => (this.editModeEnabled ? this.showDiscardDialog() : this.enableEditMode()),
      onRefresh: () => { this.reloadClicked = true; this.refetchData(); return this.refreshDone$.pipe(take(1)); },
      // Filters (event/asset types) moved above the tabs (Ray).
      onFilters: (event) => this.toggleFilters(event),
      filtersBadge: () => this.activeFilterCount,
    };
    const config = this.controlsConfig;
    queueMicrotask(() => {
      // The component may be destroyed before the microtask runs — don't
      // resurrect a cleared config (CodeRabbit).
      if (this.controlsConfig === config) {
        this.controls.setConfig(config);
        // Publish the tab's summary strip into the header bar (Ray: the
        // pacing/forecast/availability pieces moved out of this card, LEFT
        // of the bar's filter button); re-published on every compute below
        // so toggles and fresh data keep the bar in step.
        this.controls.summary.set(this.buildControlsSummary());
      }
    });

    if (this.isMonthView()) {
      this.menuOptions.unshift({
        label: 'Show Events',
        icon: this.showMonthEvents ? 'pi pi-check' : 'pi pi-fw',
        command: () => {
          this.setMaxEvents(!this.showMonthEvents);
        }
      });
    }
  }

  private initializeToggles() {
    const queryParams = this.route.snapshot.queryParams;
    
    // Helper to get value from query params or local storage
    const getValue = (key: string, storageKey: string) => {
      if (queryParams[key] !== undefined) {
        return queryParams[key] === 'true';
      }
      const stored = localStorage.getItem(storageKey);
      return stored !== null ? stored === 'true' : false;
    };

    this.showCrew = getValue('showCrew', 'schedules-show-crew');
    this.showMonthEvents = getValue('showMonthEvents', 'schedules-show-month-events');
    this.verticalView = getValue('verticalView', 'schedules-vertical-view');
    this.hideUnavailable = getValue('hideUnavailable', 'schedules-hide-unavailable');
    this.hideUnused = getValue('hideUnused', 'schedules-hide-unused');
    this.showAllDay = getValue('showAllDay', 'schedules-show-all-day');
    this.spreadRevenue = getValue('spreadRevenue', 'schedules-spread-revenue');
    this.showForecast = getValue('showForecast', 'schedules-show-forecast');
    this.showPacing = getValue('showPacing', 'schedules-show-pacing');

    // Apply initial settings to calendar options
    // Limit events when showing individual events in month view, otherwise unlimited
    this.calendarOptions.dayMaxEvents = this.showMonthEvents ? true : false;

    if (this.showAllDay) {
      this.calendarOptions.slotMinTime = '00:00:00';
      this.calendarOptions.slotMaxTime = '24:00:00';
    }
  }

  private updateToggleState(key: string, value: boolean) {
    // Update Local Storage
    localStorage.setItem(`schedules-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`, String(value));

    // Update URL
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [key]: value },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  toggleVerticalView() {
    this.verticalView = !this.verticalView;
    this.updateToggleState('verticalView', this.verticalView);
    
    // Determine current scale (day/week)
    const currentView = this.calendar.view.type;
    let scale = 'day';
    if (currentView.toLowerCase().includes('week')) {
      scale = 'week';
    }

    this.changeView(this.getViewName(scale));
    this.updateMenu();
  }

  getViewName(scale: string) {
    if (this.verticalView) {
      // Use timeGrid if vertical (resourceTimeGrid if available, but timeGrid is installed)
      return scale === 'week' ? 'timeGridWeek' : 'timeGridDay';
    }
    return scale === 'week' ? 'resourceTimelineWeek' : 'resourceTimelineDay';
  }

  toggleHideUnavailable() {
    this.hideUnavailable = !this.hideUnavailable;
    this.updateToggleState('hideUnavailable', this.hideUnavailable);
    this.convertAssetsIntoResources(); // Re-render resources
    this.updateMenu();
  }

  toggleHideUnused() {
    this.hideUnused = !this.hideUnused;
    this.updateToggleState('hideUnused', this.hideUnused);
    this.convertAssetsIntoResources(); // Re-render resources
    this.updateMenu();
  }

  toggleShowAllDay() {
    this.showAllDay = !this.showAllDay;
    this.updateToggleState('showAllDay', this.showAllDay);
    
    if (this.showAllDay) {
      this.calendar.setOption('slotMinTime', '00:00:00');
      this.calendar.setOption('slotMaxTime', '24:00:00');
    } else {
      this.calendar.setOption('slotMinTime', '06:00:00');
      this.calendar.setOption('slotMaxTime', '22:00:00');
    }
    this.updateMenu();
  }

  toggleSpreadRevenue() {
    this.spreadRevenue = !this.spreadRevenue;
    this.updateToggleState('spreadRevenue', this.spreadRevenue);
    this.updateMenu();
    if (this.isMonthView() && !this.showMonthEvents) {
      this.refetchData();
    } else {
      this.calculateViewTotal();
    }
  }

  toggleShowForecast() {
    this.showForecast = !this.showForecast;
    this.updateToggleState('showForecast', this.showForecast);
    this.updateMenu();
    this.retrieveViewDates();
    this.retrieveProphetMetrics();
  }

  toggleShowPacing() {
    this.showPacing = !this.showPacing;
    this.updateToggleState('showPacing', this.showPacing);
    this.updateMenu();
    this.retrieveViewDates();
  }

  openIcalDialog() {
    this.freyaMutate.openMutateObject({
      mutateType: 'create',
      objectType: 'ical',
      additionalValues: [{
        property: 'assetId',
        value: undefined,
      }],
    });
  }

  openCalendarRemarksDialog() {
    // getDate() is a FullCalendar instant; format it in the calendar's
    // timezone so the remarks dialog opens on the same day the schedule
    // page is displaying (the browser's local timezone may differ).
    const date = this.calendar
      ? dayjs(this.calendar.getDate()).tz(this.getCalendarTimezone()).format('YYYY-MM-DD')
      : null;
    this.store.dispatch(DispatchCalendarRemarksDialogActions.dialogOpened({ date }));
  }

  private changeUrl(date: string, viewType: keyof typeof REVERSE_SCHEDULE_VIEW_TYPE_MAP) {
    if (!date && !viewType) { return; }

    // The week view's record window is the next 7 days from the anchor
    // (Ray); single-day/month views must clear a stray end. The end is
    // sent explicitly: a merge-only nav replays Router-side params the
    // service's replaceState writes are invisible to, which would drop or
    // resurrect dateTo (same clobber class as the dispatch changeUrl fix).
    const dateTo = viewType === 'week' && date
      ? dayjs(date).add(6, 'day').format('YYYY-MM-DD')
      : null;
    this.router.navigate(['schedule', viewType], { queryParams: { date, dateTo }, queryParamsHandling: 'merge' });
  }

  private getLastSavedDate(): string {
    // The date the calendar opens at. An explicit `?date=` URL param — a
    // bookmark or deep link — wins over the session's last-used date
    // (CodeRabbit round 3: a stored `schedule-date` used to beat the URL,
    // run-log row repro'd twice); the saved date stands in when the URL
    // carries none, then today.
    return selectScheduleSeedDate(
      this.route.snapshot.queryParamMap.get('date'),
      sessionStorage.getItem(environment.lskeys.scheduleDate),
    );
  }

  openDynamicReports() {
    if (!this.calendar?.view) { return; }

    // Use currentStart/currentEnd (strict range) rather than activeStart/activeEnd.
    // activeStart/activeEnd includes padded days from adjacent months in month view,
    // which would make the report date range not match what is visible on the schedule.
    const start = dayjs(this.calendar.view.currentStart);
    const end = dayjs(this.calendar.view.currentEnd).subtract(1, 'day');

    // Columns relevant when viewing the schedule: event identity, asset, job/customer,
    // revenue figures, crew and duration — all match the schedule card display.
    const dataColumns = [
      'event',
      'eventType',
      'eventStatus',
      'eventBookingDate',
      'assets',
      'job',
      'customer',
      'crewMembers',
      'eventDurationInMinutes',
      'subTotal',
      'discountTotal',
      'taxTotal',
      'total',
    ];

    const queryParams = {
      zone: this.brandingService.currentZone().value?.id,
      period: 'Custom',
      customPeriod: `${start.format(DATE_PARAM_FORMAT)};${end.format(DATE_PARAM_FORMAT)}`,
      // eventStart matches events whose scheduled start date falls within the range,
      // matching exactly what the schedule calendar displays.
      periodType: 'eventStart',
      eventStatuses: ['booked', 'confirmed', 'completed'],
      groupBy: 'event',
      dataColumns,
    };

    const urlTree = this.router.createUrlTree(['/reports', 'dynamic', 'new'], { queryParams });
    const url = this.router.serializeUrl(urlTree);
    window.open(url, '_blank');
  }

  copyScheduleHtml() {
    const exportableEvents: ExportableEvent[] = this.calendarEvents
      .filter(event => event.type !== 'book-off')
      .map(event => {
      const durationHours = (event.end - event.start) / 3600;
      
      let totalLaborCost = 0;
      const crewNames = [];
      let crewCount = 0;

      if (event.attendees) {
        event.attendees.forEach(attendee => {
          if (attendee.role === EventAttendeeRoles.author) return;
          
          crewCount++;
          crewNames.push(`${formatUserName(attendee.user)} (${strToTitleCase(attendee.role)})`);

          const userRate = (attendee.user as any).rateHourly || 0;
          // Simple cost calculation: Rate * Duration
          // Note: This does not account for overtime rules or complex pay structures available in Dispatch
          totalLaborCost += (userRate * durationHours);
        });
      }

      const revenue = event.discountedSubTotal || 0;
      const laborPercentage = revenue ? ((totalLaborCost / revenue) * 100).toFixed(1) + '%' : '0%';
      const trucks = event.assets?.filter(a => ['Truck', 'Delivery'].includes(a.type)) || [];

      return {
        startTime: dayjs.tz(event.start * 1000, this.timezone).format('h:mm A'),
        jobCode: event.job?.code || '',
        jobUrl: event.job?.id ? `${window.location.origin}/jobs/${event.job.id}` : '',
        type: strToTitleCase(event.type),
        status: strToTitleCase(event.status),
        customerName: this.fcHelper.resolveTitle(event), // Use helper to resolve customer/title
        revenue: revenue,
        currency: event.job?.currency || 'USD',
        totalLaborCost: totalLaborCost,
        laborPercentage: laborPercentage,
        trucks: trucks.map(t => t.name).join(', '),
        crew: crewNames.join(', '),
        truckCount: trucks.length,
        crewCount: crewCount
      };
    });

    const html = generateScheduleHtml(exportableEvents);
    this.writeToClipboard(html);
  }

  private async writeToClipboard(content: string) {
    try {
      const blobHtml = new Blob([content], { type: 'text/html' });
      const blobText = new Blob([content], { type: 'text/plain' });
      const data = [new ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobText })];
      await navigator.clipboard.write(data);
      this.localNotify.success('Schedule copied to clipboard');
    } catch (err) {
      console.error('Failed to copy: ', err);
      this.localNotify.error('Failed to copy schedule');
    }
  }

  private updateHeaderHeight(target?: HTMLElement) {
    const el = target || this.el.nativeElement.querySelector('.calendar-header-wrapper');
    if (!el) return;

    const style = window.getComputedStyle(el);
    const marginTop = parseFloat(style.marginTop) || 0;
    const marginBottom = parseFloat(style.marginBottom) || 0;

    // Use offsetHeight (border-box) + margins to ensure full spacing
    const totalHeight = el.offsetHeight + marginTop + marginBottom;
    this.el.nativeElement.style.setProperty('--calendar-header-height', `${totalHeight}px`);
  }
}
